import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../../auth/apiFetch.ts';
import { SurfaceShell } from '../../components/SurfaceShell.tsx';
import { Panel } from '../../components/Panel.tsx';
import { Button, IconButton } from '../../components/Button.tsx';
import { DataTable, type Column } from '../../components/DataTable.tsx';
import { StatusDot } from '../../components/StatusDot.tsx';
import { StateLabel } from '../../components/StatusBadge.tsx';
import { Input, Select } from '../../components/Field.tsx';
import { Empty, ErrorLine } from '../../components/Empty.tsx';
import {
  Blank, ReadBody, SkeletonRail, SkeletonTable, StoppedContext, faultOf,
  stateWord, unread, useRead, type Read as StateRead, type SkeletonColumn,
} from '../../components/states/index.ts';
import { Distribution } from '../../components/Distribution.tsx';
import { Timestamp } from '../../components/Timestamp.tsx';
import { LiveTag } from '../../components/Freshness.tsx';
import { RobotLink } from '../../components/AppLink.tsx';
import { useFeedWord } from '../../shell/TopBar.tsx';
import { Icon } from '../../shell/Icon.tsx';
import {
  bytes, duration, midEllipsis, NO_VALUE, relative, utc,
} from '../../components/format.ts';
import {
  LIVENESS_STATE,
  matrixTone,
  STATUS_STATE,
  type Tone,
} from '../../components/state.ts';
import { notifyError, notifyOk } from '../../components/toastStore.ts';
import { useNow } from '../../components/useNow.ts';
import { useBrowserRoute } from '../../router/useBrowserRoute.ts';
import { buildPath, parsePath } from '../../router/parsePath.ts';
import { useRobots, type Robot } from '../../hooks/useRobots.ts';
import {
  useCreateMorphology,
  useCreateVersion,
  useMorphologies,
  type CatalogVersion,
  type Container,
  type ContainerRole,
  type Manifest,
} from '../../hooks/useCatalog.ts';
import {
  useBringupContent,
  useSyncBringupPayload,
  type BringupContent,
} from '../../hooks/useBringup.ts';
import { defaultBringupScript } from './defaultBringupScript.ts';
import styles from './CatalogWorkspace.module.css';

/* ==========================================================================
   CATALOG · morphology → hardware revision → container manifest → bringup.

   This surface publishes the thing a freshly flashed robot downloads and runs
   as root. Four decisions follow from that:

   1  THE HIERARCHY IS A TREE, NOT TWO LISTS. One 300px rail holds every
      morphology and every revision under it, each revision carrying its three
      readiness cells and the number of robots pinned to it — so "which
      revisions are unfinished, and is anything running them" is answered
      before a click (palantir-gotham-map-workspace.png's numbered layer tree,
      rerun-kissicp-blueprint-tree-viewer.png's 23px blueprint rail).

   2  READINESS IS EVIDENCE, NOT A WORD. `complete` and `bringup_ready` are
      server-computed booleans; showing only the verdict makes an operator
      guess which half is missing. The Readiness panel is the flight-log
      review's per-section verdict list (auterion-flight-log-review.png):
      manifest / script / payload each with its own dot AND its own count or
      digest, then the verdict underneath.

   3  EDITING IS A DOCUMENT (viam-machine-config-console.png). Nothing is
      committed as you type. The manifest and the script accumulate into one
      staged change with a single pinned primary — `Publish ⌘S` — that names
      what it will write and how many robots are pinned to the revision it
      writes to. Formant's CANCEL/SEND PATH pair, applied to config.

   4  THE MANIFEST IS A LEDGER. Image refs are long, and the question an
      engineer actually has is "what moved, and when" — so the editable image
      column is followed by one column per *other* revision of the morphology,
      newest to oldest, each named by its revision and carrying only the half
      of the ref that differs, with `=` where nothing moved. Four containers
      across four revisions is sixteen readings in one glance, and the rows
      that are not `=` are the release. Secondary columns shed oldest-first on
      a narrow monitor (DataTable's `drop`), so the ref column never starves.

   The bringup script gets a real code surface: line numbers, a shell
   highlighter, horizontal scroll, copy, and the stored object's sha256 in the
   panel header — because the digest is the load-bearing half of the contract.
   ========================================================================== */

/* -- the contract, as the API actually returns it --------------------------
   `CatalogVersion` in useCatalog.ts predates the digest fields; the endpoint
   returns them (they are what PayloadDigest already reads), so they are
   declared here rather than guessed at the call site. */
interface CatalogRevision extends CatalogVersion {
  script_sha256?: string | null | undefined;
  payload_key?: string | null | undefined;
  payload_sha256?: string | null | undefined;
}

const ROLES: readonly ContainerRole[] = ['both', 'bootstrap', 'update'];

/** A container the operator has staged but not published. It rides in the same
    list as the stored ones with a NEGATIVE id, so the table, the completeness
    count and the blocked-row check stay one code path over one list, and
    `id < 0` is the only place "not written yet" is expressed. */
interface StagedContainer {
  id: number;
  name: string;
  role: ContainerRole;
}

let stagedSeq = 0;
function nextStagedId(): number {
  stagedSeq -= 1;
  return stagedSeq;
}

function stagedContainer(a: StagedContainer): Container {
  return {
    id: a.id,
    version_id: -1,
    name: a.name,
    role: a.role,
    ordinal: 0,
    value: null,
    meta: {},
  };
}

/** One dirty revision, graded against its own manifest read. The Publish verb
    and the rail's review block are the same list read twice, so what the verb
    is about to write and what the rail says it will write cannot disagree. */
interface Staged {
  key: string;
  morphology: string;
  version: string;
  count: number;
  /** `manifest`, `script`, or both. */
  parts: string[];
  /** Name of a container this revision would publish with no image. */
  blocked: string | null;
  /** How many there are. One name is actionable; three names is a paragraph,
      and naming only the first of three is a claim that there is one. */
  blockedCount: number;
  emptied: boolean;
  unverifiable: boolean;
  /** `null` — the install base has not been read. */
  robots: number | null;
}

/** Why this revision cannot be published, in the words the operator needs. */
function haltReason(x: Staged): string | null {
  return x.unverifiable
    ? 'manifest unread'
    : x.emptied
      ? 'manifest would be empty'
      : x.blocked === null
        ? null
        : x.blockedCount > 1
          ? `${x.blockedCount} containers: no image`
          : `${x.blocked}: no image`;
}

/* ==========================================================================
   THE STAGED CHANGE SET

   A draft belongs to a REVISION, not to "whatever the rail has selected".
   That distinction is the whole of this block. An operator who bumps the
   agent image on jetrover/rev-d, clicks rev-c to check what IT pins, and
   comes back has to find the bump still sitting there — the alternative is a
   surface that throws away an edit to a root-privileged record on a
   navigation click, silently, with Publish going grey on the way past.

   So the drafts are a map keyed by `morphology/version`, every dirty revision
   keeps its own until it is published or discarded, the rail marks each of
   them, and Publish counts and commits all of them. A draft that has been
   edited back to what is stored is DELETED rather than kept as an empty
   record, so "is this revision dirty" is `drafts.has(key)` and can never
   drift from what the counters say.
   ========================================================================== */

interface Draft {
  morphology: string;
  version: string;
  /** container id → the staged image ref. Only genuine departures from the
      stored value live here; typing a value back to what is stored removes
      the entry, so the count is always the number of things that will move. */
  images: Readonly<Record<number, string>>;
  /** Stored container ids staged for removal. Nothing has been sent. */
  removing: ReadonlySet<number>;
  adds: readonly StagedContainer[];
  /** `null` = the operator has not moved this revision's script off stored. */
  script: string | null;
}

const BLANK_DRAFT: Draft = {
  morphology: '',
  version: '',
  images: {},
  removing: new Set<number>(),
  adds: [],
  script: null,
};

function keyOf(morphology: string, version: string): string {
  return `${morphology}/${version}`;
}

function blankDraft(morphology: string, version: string): Draft {
  return { ...BLANK_DRAFT, morphology, version };
}

function touchesManifest(d: Draft): boolean {
  return (
    Object.keys(d.images).length > 0 || d.removing.size > 0 || d.adds.length > 0
  );
}

function draftClean(d: Draft): boolean {
  return !touchesManifest(d) && d.script === null;
}

/** Staged mutations, counted the way an operator counts them: one per image
    that will publish differently, one per container leaving, one per container
    arriving, one for the script. A staged container that also has an image
    typed into it is ONE arrival, not two — hence the `id >= 0` filter. */
function draftCount(d: Draft): number {
  let n = 0;
  for (const id of Object.keys(d.images)) if (Number(id) >= 0) n += 1;
  return n + d.removing.size + d.adds.length + (d.script === null ? 0 : 1);
}

function draftParts(d: Draft): string[] {
  return [
    ...(touchesManifest(d) ? ['manifest'] : []),
    ...(d.script === null ? [] : ['script']),
  ];
}

/* ---- writing a change set ------------------------------------------------
   Plain functions rather than per-revision mutation hooks: a change set can
   span several revisions and several morphologies, and a hook bound to one of
   them at call time cannot write the others. Invalidation is done once, by
   the caller, across the whole catalog. */

async function send(path: string, method: string, body?: unknown): Promise<unknown> {
  const r = await apiFetch(path, {
    method,
    ...(body === undefined
      ? {}
      : {
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        }),
  });
  if (!r.ok && r.status !== 204) throw new Error(await detailOf(r));
  if (r.status === 204) return null;
  return r.json().catch(() => null);
}

/** Removals, then creations, then ONE PUT of every value the revision ends up
    holding — so the server's "every container is valued" check runs against
    exactly the set the operator reviewed. Each step awaits: a half-applied
    manifest is the one outcome worth ordering the calls to avoid. */
async function publishRevision(
  d: Draft,
  stored: readonly Container[],
): Promise<void> {
  const base =
    `/api/morphologies/${encodeURIComponent(d.morphology)}` +
    `/versions/${encodeURIComponent(d.version)}`;
  if (touchesManifest(d)) {
    for (const id of d.removing) {
      if (id >= 0) await send(`${base}/containers/${id}`, 'DELETE');
    }
    const values = stored
      .filter((c) => !d.removing.has(c.id))
      .map((c) => ({ id: c.id, value: (d.images[c.id] ?? c.value ?? '').trim() }));
    for (const a of d.adds) {
      const body = (await send(`${base}/containers`, 'POST', {
        name: a.name,
        role: a.role,
      })) as { containers?: Container[] } | null;
      /* The POST answers with the revision's whole manifest, so the new row's
         server id is read back rather than guessed. */
      const made = body?.containers?.find(
        (c) => c.name === a.name && c.role === a.role,
      );
      if (made !== undefined) {
        values.push({ id: made.id, value: (d.images[a.id] ?? '').trim() });
      }
    }
    await send(`${base}/manifest`, 'PUT', { containers: values });
  }
  if (d.script !== null) {
    await send(`${base}/script/content`, 'PUT', { content: d.script });
  }
}

/** Same key and same shape as useCatalog's `useVersions`, so the two share one
    cache entry and every existing invalidation still lands. */
async function fetchVersions(morphology: string): Promise<CatalogRevision[]> {
  const r = await apiFetch(
    `/api/morphologies/${encodeURIComponent(morphology)}/versions`,
  );
  if (!r.ok) throw new Error(await detailOf(r));
  const body = (await r.json()) as { versions?: CatalogRevision[] };
  return body.versions ?? [];
}

/** Same key and shape as useCatalog's `useContainers`, so the ledger's reads
    and the editor's read are one cache entry. */
async function fetchManifest(
  morphology: string,
  version: string,
): Promise<Manifest> {
  const r = await apiFetch(
    `/api/morphologies/${encodeURIComponent(morphology)}` +
      `/versions/${encodeURIComponent(version)}/containers`,
  );
  if (!r.ok) throw new Error(await detailOf(r));
  const body = (await r.json()) as { containers?: Container[]; complete?: boolean };
  return { containers: body.containers ?? [], complete: body.complete ?? false };
}

function metaString(meta: Record<string, unknown>, key: string): string | null {
  const v = meta[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/** `ghcr.io/forest3ai/f3-capture:1.6.4` → `['ghcr.io/forest3ai/f3-capture', '1.6.4']` */
function splitRef(ref: string): [string, string | null] {
  const slash = ref.lastIndexOf('/');
  const colon = ref.lastIndexOf(':');
  if (colon > slash) return [ref.slice(0, colon), ref.slice(colon + 1)];
  return [ref, null];
}

/* ==========================================================================
   THE THIRD RENDER STATE — unread.

   A fetch that failed is not an empty result, and this is the surface where
   the difference decides whether a robot provisions. `0/0 pinned` over a 500
   is a confident summary of nothing; `no robots pinned` over a dead
   /api/robots is a claim the console has no standing to make; a green
   `bringup ready` computed from a manifest we could not read is the worst of
   the three, because it is the one an operator would act on.

   So every read on this surface resolves to one of four states, and every
   readout derived from one refuses to conclude unless it is actually holding
   data. `held()` is the only way to get at it, and it returns `null` — never
   an empty array, never a zero — when there is nothing to hold. The shell
   already does exactly this two rows above the workspace (`— faulted`,
   `—/— out of limits`); this is that rule, applied inside the catalog.
   ========================================================================== */

type Read<T> =
  | { st: 'loading' }
  | { st: 'ok'; data: T }
  /** The last read failed but an earlier one landed: show it, and say so. */
  | { st: 'stale'; data: T; why: string }
  | { st: 'failed'; why: string };

const LOADING = { st: 'loading' } as const;

/* ONE EVENT, ONE WORDING. `Failed to fetch` is a browser exception, not a
   sentence for an operator — and printing it in a rail 400px above a supply
   ledger that calls the same event `no reply` is one fault described twice.
   `stateWord` is where the console keeps those four words. */
function reason(err: unknown): string {
  const fault = faultOf(err);
  return fault === null ? 'read failed' : stateWord(fault.class);
}

function readOf<T>(q: {
  data: T | undefined;
  isError: boolean;
  error: unknown;
}): Read<T> {
  if (q.isError) {
    return q.data === undefined
      ? { st: 'failed', why: reason(q.error) }
      : { st: 'stale', data: q.data, why: reason(q.error) };
  }
  return q.data === undefined ? LOADING : { st: 'ok', data: q.data };
}

/* The container manifest's own columns, so the panel is the same instrument
   whether or not a revision is on screen. */
const MANIFEST_COLUMNS: readonly SkeletonColumn[] = [
  { label: 'Container', width: '10ch' },
  { label: 'Image' },
  { label: 'rev-c', width: '9ch', align: 'right' },
  { label: 'rev-b', width: '9ch', align: 'right' },
  { label: '001', width: '9ch', align: 'right' },
];

/** The data, or `null` when we hold none. Never a fabricated default. */
function held<T>(r: Read<T>): T | null {
  return r.st === 'ok' || r.st === 'stale' ? r.data : null;
}

/** The message behind a failed read, for an inline error row. */
function failure(r: Read<unknown>): string | null {
  return r.st === 'failed' || r.st === 'stale' ? r.why : null;
}

/** `4 containers` / `— containers`. A count we do not have is an em dash. */
function plural(n: number | null, one: string, many = `${one}s`): string {
  return n === null ? `${NO_VALUE} ${many}` : `${n} ${n === 1 ? one : many}`;
}

/* ==========================================================================
   ROW SCROLL — the honest bounded table.

   A panel of fixed height holding a table of unknown length has exactly two
   ways to lie: paint half a row at its bottom edge, and print `(19)` in its
   header over five rows with no sign that fourteen more exist. Both were true
   of this surface. The gap between the two facts is not cosmetic — the header
   of the install-base panel is the blast radius of a root-privileged publish,
   and an operator who reads `5` off a panel that means `19` will publish
   against a number that is wrong by a factor of four.

   So the scroll region owns its own geometry:

   · it measures the box it was flexed into, and gives the scrolling viewport
     an EXACT multiple of the row pitch (plus the sticky header) — a row is
     therefore never sliced by a panel edge, only ever fully in or fully out;
   · what does not fit is named, not hidden: a 20px footer strip prints the
     visible range against the total (`1–12 of 19`) and pages through it, so
     the header's count and the painted rows can be reconciled without
     hovering anything (foxglove-state-transitions-timeline.png answers the
     same problem with `(Zoom for more)`);
   · and the gutter is painted permanently rather than as an overlay that
     fades — see `.rowScrollView` in the stylesheet.
   ========================================================================== */

/** Panel chrome above the first row: border + 28px header + rule + 22px thead. */
const PANEL_CHROME = 53;
const ROW_H = 26;
const THEAD_H = 22;
const MORE_H = 20;
/** The readiness line plus its two borders. The revision's NAME is not in
    here: it rides in the page head's subtitle beside the surface title, the
    way every other detail surface in this console carries its object
    (`fleet/views/RobotDetail.tsx`). The head is a fixed 40px whether or not
    anything is in it, and this frame was spending it on the single word
    `Hardware revisions` while printing `rev-d jetrover …` 28px below — one
    band of chrome saying nothing over another band saying what the first one
    had room for. One line, and then the surface is data. */
const IDENT_H = 28;
/** The code surface stops being a code surface below about a dozen lines. */
const SCRIPT_MIN = 300;
/** Past this the band is eating the script pane. What does not fit is named
    and paged by the panel's own footer strip, never silently clipped. */
const BAND_ROWS_MAX = 9;

function clampInt(lo: number, n: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** The height a panel needs to paint `rows` rows, capped at `cap`. */
function panelHeightFor(rows: number, cap: number): number {
  const shown = clampInt(1, rows, cap);
  return PANEL_CHROME + shown * ROW_H + (rows > shown ? MORE_H : 0);
}

/** The measured height of an element, tracked. 0 until it is in the document. */
function useBoxHeight(): [(el: HTMLElement | null) => void, number] {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [h, setH] = useState(0);
  useEffect(() => {
    if (el === null) return;
    setH(el.clientHeight);
    const ro = new ResizeObserver(() => setH(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, h];
}

interface Metrics {
  top: number;
  view: number;
  total: number;
}

/** A scrolling element's position, tracked through scroll, resize and content. */
function useScrollMetrics(el: HTMLElement | null): Metrics {
  const [m, setM] = useState<Metrics>({ top: 0, view: 0, total: 0 });
  useEffect(() => {
    if (el === null) return;
    const read = () =>
      setM((prev) =>
        prev.top === el.scrollTop &&
        prev.view === el.clientHeight &&
        prev.total === el.scrollHeight
          ? prev
          : { top: el.scrollTop, view: el.clientHeight, total: el.scrollHeight },
      );
    read();
    el.addEventListener('scroll', read, { passive: true });
    const ro = new ResizeObserver(read);
    ro.observe(el);
    const mo = new MutationObserver(read);
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      el.removeEventListener('scroll', read);
      ro.disconnect();
      mo.disconnect();
    };
  }, [el]);
  return m;
}

/**
 * A painted scroll rail. The platform's own scrollbar here is an OVERLAY that
 * fades a second after the wheel stops — which is precisely when a region
 * whose header says `(19)` over eight rows needs to admit that it scrolls. So
 * the native one is suppressed in CSS and this is painted instead, permanently
 * and with position, the way Gotham's facet rail carries a proportional bar
 * beside every count (palantir-gotham-facet-rail-timeline.png).
 */
function ScrollTrack({ metrics, inset = 0 }: { metrics: Metrics; inset?: number }) {
  const { top, view, total } = metrics;
  if (total <= view + 1 || view === 0) return null;
  const height = Math.max(8, (view / total) * 100);
  const at = Math.min(100 - height, (top / total) * 100);
  return (
    <div
      className={styles.scrollRail}
      style={{ ['--rail-inset' as string]: `${inset}px` }}
      aria-hidden="true"
    >
      <span className={styles.scrollThumb} style={{ top: `${at}%`, height: `${height}%` }} />
    </div>
  );
}

function RowScroll({
  rows,
  noun,
  children,
}: {
  /** How many rows the table below actually has. */
  rows: number;
  /** Plural noun for the footer's accessible name — `robots`, `containers`. */
  noun: string;
  children: ReactNode;
}) {
  const [boxRef, boxH] = useBoxHeight();
  const [view, setView] = useState<HTMLDivElement | null>(null);
  const metrics = useScrollMetrics(view);

  /* Two caps, because the footer strip costs 20px and only exists when it is
     needed: fit without it if the rows fit without it. */
  const room = (extra: number) => Math.floor((boxH - extra - THEAD_H) / ROW_H);
  const capBare = boxH > 0 ? Math.max(1, room(0)) : Math.max(1, rows);
  const over = boxH > 0 && rows > capBare;
  const cap = over ? Math.max(1, room(MORE_H)) : capBare;
  const shown = Math.max(1, Math.min(rows, cap));
  /* An exact multiple of the pitch: the viewport can only ever end on a row
     boundary, so the panel's bottom edge cannot cut one in half. */
  const viewH = boxH > 0 ? THEAD_H + shown * ROW_H : undefined;

  const first = Math.min(
    Math.floor(metrics.top / ROW_H) + 1,
    Math.max(1, rows - shown + 1),
  );
  const last = Math.min(first + shown - 1, rows);
  const atEnd = last >= rows;

  const page = () => {
    if (view === null) return;
    view.scrollTo({ top: atEnd ? 0 : (last - 1) * ROW_H, behavior: 'auto' });
  };

  return (
    <div className={styles.rowScroll} ref={boxRef}>
      <div
        className={styles.rowScrollBody}
        style={viewH === undefined ? undefined : { height: `${viewH}px` }}
      >
        <div className={styles.rowScrollView} ref={setView}>
          {children}
        </div>
        <ScrollTrack metrics={metrics} inset={THEAD_H} />
      </div>
      {over && (
        <button
          type="button"
          className={styles.moreBar}
          onClick={page}
          aria-label={
            atEnd
              ? `Back to the first ${shown} of ${rows} ${noun}`
              : `Show the next ${Math.min(shown, rows - last)} of ${rows} ${noun}`
          }
          title={
            atEnd
              ? `Showing the last ${shown} of ${rows} — click for the first`
              : `${rows - last} more below — click to page down`
          }
        >
          <span className={styles.moreRange}>
            {first}–{last} of {rows}
          </span>
          <span className={styles.grow} />
          <span className={styles.moreVerb}>{atEnd ? 'top' : `+${rows - last}`}</span>
          <Icon name={atEnd ? 'chevron-up' : 'chevron-down'} size={12} />
        </button>
      )}
    </div>
  );
}

/** The API's own message, so an inline row says `db timeout`, not `status 500`. */
async function detailOf(r: Response): Promise<string> {
  const body = (await r.json().catch(() => null)) as { detail?: unknown } | null;
  return typeof body?.detail === 'string' && body.detail !== ''
    ? body.detail
    : `status ${r.status}`;
}

/* ==========================================================================
   The workspace
   ========================================================================== */

export function CatalogWorkspace() {
  const { path, setPath } = useBrowserRoute();
  const { segments } = parsePath(path);

  const morphQuery = useMorphologies();
  /* The console's one failure language. This surface used to answer a dead
     `/api/morphologies` by returning a page containing a single --error
     sentence — no rail, no revision panel, no column of published versions,
     846px of ground and the raw `Failed to fetch` printed at an operator. The
     rail keeps its instrument now and the fault is one line inside it. */
  const catalogRead = useRead(morphQuery, {
    path: '/api/morphologies',
    subject: 'the hardware catalog',
    empty: morphQuery.data !== undefined && morphQuery.data.length === 0,
  });
  const morphList = useMemo(() => morphQuery.data ?? [], [morphQuery.data]);

  /* Every morphology's revisions at once: the rail is a tree, and a tree that
     only knows the branch you already opened is a two-list master/detail
     wearing a disclosure triangle.

     The map holds a READ per morphology rather than a list per morphology: a
     morphology whose revisions did not come back has no revisions we can
     name, which is not the same fact as having none. */
  const versionQueries = useQueries({
    queries: morphList.map((m) => ({
      queryKey: ['catalog-versions', m.morphology_id] as const,
      queryFn: () => fetchVersions(m.morphology_id),
    })),
  });
  /* One string that changes exactly when any branch's read changes — data or
     error. Extracted so the dependency is statically checkable. */
  const versionStamp = versionQueries
    .map((q) => `${q.dataUpdatedAt}:${q.errorUpdatedAt}`)
    .join('|');
  const byMorph = useMemo(() => {
    const map = new Map<string, Read<CatalogRevision[]>>();
    morphList.forEach((m, i) => {
      const q = versionQueries[i];
      map.set(m.morphology_id, q === undefined ? LOADING : readOf(q));
    });
    return map;
    // versionQueries is a fresh array each render; its state is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [morphList, versionStamp]);

  /* The tree is one read, spread over N requests: until every branch has
     answered, "there is no revision here" is not a statement we can make. */
  const catalogPending =
    morphQuery.isPending ||
    Array.from(byMorph.values()).some((r) => r.st === 'loading');

  /* Every revision in the catalog, flat and in rail order. The change set is
     keyed by revision, so the accounting behind Publish has to be able to
     reach a revision the rail is not currently pointing at. */
  const allRevisions = useMemo(() => {
    const out: { morphology: string; version: string; revision: CatalogRevision }[] = [];
    for (const m of morphList) {
      for (const r of held(byMorph.get(m.morphology_id) ?? LOADING) ?? []) {
        out.push({ morphology: m.morphology_id, version: r.version, revision: r });
      }
    }
    return out;
  }, [morphList, byMorph]);

  /* EVERY revision's manifest, not just the selected one's. Three readouts
     depend on it and they must not be able to disagree: the ledger columns in
     the editor, the `edited` marks in the rail, and what Publish is about to
     write. Same query key as `useContainers`, so this is one cache entry per
     revision and one invalidation. */
  const manifestQueries = useQueries({
    queries: allRevisions.map((e) => ({
      queryKey: ['catalog-containers', e.morphology, e.version] as const,
      queryFn: () => fetchManifest(e.morphology, e.version),
    })),
  });
  const manifestStamp = manifestQueries
    .map((q) => `${q.dataUpdatedAt}:${q.errorUpdatedAt}`)
    .join('|');
  const manifests = useMemo(() => {
    const map = new Map<string, Read<Manifest>>();
    allRevisions.forEach((e, i) => {
      const q = manifestQueries[i];
      map.set(keyOf(e.morphology, e.version), q === undefined ? LOADING : readOf(q));
    });
    return map;
    // manifestQueries is a fresh array each render; its state is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRevisions, manifestStamp]);

  /* ---- selection lives in the URL: /catalog/jetrover/rev-d --------------- */
  const morphology =
    segments[0] !== undefined && byMorph.has(segments[0])
      ? segments[0]
      : morphList[0]?.morphology_id;
  const revisions = useMemo(
    () =>
      morphology === undefined
        ? []
        : (held(byMorph.get(morphology) ?? LOADING) ?? []),
    [morphology, byMorph],
  );
  const version =
    segments[1] !== undefined && revisions.some((r) => r.version === segments[1])
      ? segments[1]
      : revisions[0]?.version;
  const revision = revisions.find((r) => r.version === version) ?? null;

  const select = useCallback(
    (m: string, v?: string) => {
      setPath(v === undefined ? buildPath('catalog', m) : buildPath('catalog', m, v));
    },
    [setPath],
  );

  /* ---- who is pinned to what --------------------------------------------
     `null` all the way down when the fleet has not been read: the index, the
     per-revision counts and the install base are all one read, and none of
     them may fall back to zero. */
  const fleetQuery = useRobots();
  const fleetRead = readOf(fleetQuery);
  const fleet = held(fleetRead);
  const byRevisionId = useMemo(() => {
    if (fleet === null) return null;
    const map = new Map<number, Robot[]>();
    for (const r of fleet) {
      if (r.morphology_version_id === null) continue;
      const list = map.get(r.morphology_version_id);
      if (list === undefined) map.set(r.morphology_version_id, [r]);
      else list.push(r);
    }
    return map;
  }, [fleet]);
  const pinned =
    revision === null || byRevisionId === null
      ? null
      : (byRevisionId.get(revision.id) ?? []);

  /* ---- the staged change set --------------------------------------------
     Keyed by revision (see `Draft` above), so navigating the rail is a read,
     never a discard. */
  const revKey = keyOf(morphology ?? '', version ?? '');
  const [drafts, setDrafts] = useState<ReadonlyMap<string, Draft>>(() => new Map());
  const draft = drafts.get(revKey) ?? BLANK_DRAFT;

  const editDraft = useCallback(
    (m: string, v: string, fn: (d: Draft) => Draft) => {
      const key = keyOf(m, v);
      setDrafts((prev) => {
        const next = fn(prev.get(key) ?? blankDraft(m, v));
        const map = new Map(prev);
        /* Edited back to stored is not "dirty with no changes" — it is clean,
           and the entry goes, so `drafts.has(key)` is the only definition of
           dirty this surface has. */
        if (draftClean(next)) map.delete(key);
        else map.set(key, next);
        return map;
      });
    },
    [],
  );
  const stage = useCallback(
    (fn: (d: Draft) => Draft) => {
      if (morphology === undefined || version === undefined) return;
      editDraft(morphology, version, fn);
    },
    [editDraft, morphology, version],
  );
  /* `morphology/version` → staged mutations. One derivation, read by the rail's
     marks, the ledger's column headers and the Publish badge, so those three
     can never disagree about which revisions are dirty. */
  const stagedByKey = useMemo(
    () =>
      new Map(Array.from(drafts.entries(), ([k, d]) => [k, draftCount(d)] as const)),
    [drafts],
  );

  /* The diff target is a VIEW of this revision, not an edit to it, so unlike
     the draft it belongs to the selection and resets with it. */
  const [compareSel, setCompareSel] = useState<string | null>(null);
  useEffect(() => {
    setCompareSel(null);
  }, [revKey]);

  const manifestRead = manifests.get(revKey) ?? LOADING;
  const manifest = held(manifestRead);
  const manifestUnread = manifest === null;
  const stored = useMemo(() => manifest?.containers ?? [], [manifest]);
  /* Staged additions ride in the table as containers with a negative id — the
     table, the completeness count and the blocked-row check are then one code
     path over one list, and `id < 0` is the single place "not written yet"
     lives. */
  const containers = useMemo(
    () => [...stored, ...draft.adds.map(stagedContainer)],
    [stored, draft.adds],
  );
  const live = useMemo(
    () => containers.filter((c) => !draft.removing.has(c.id)),
    [containers, draft.removing],
  );
  const imageOf = useCallback(
    (c: Container) => draft.images[c.id] ?? c.value ?? '',
    [draft.images],
  );

  /* ---- the state band's height ------------------------------------------
     The band used to be a hard 190px whatever it held, which is a number that
     happens to fit four containers and five robots and silently amputates the
     sixth of anything. It is now a function of what this revision actually
     holds, quantised to whole rows, and bounded at both ends: never shorter
     than the readiness list's own content, never so tall that the bringup
     script — the thing a robot runs as root — stops being a code surface.

     It is derived from the SELECTED revision rather than from the catalog's
     worst case on purpose: sizing every revision's band to the largest install
     base in the fleet would buy a permanent 300px of empty table under a
     four-container manifest, which is the same defect pointing the other way. */
  const [mainRef, mainH] = useBoxHeight();
  const [wide, setWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1590px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1590px)');
    const onChange = () => setWide(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  /* NO PANEL EXPANDS OVER ANOTHER ONE HERE. Both `expand` verbs are gone —
     the manifest's and the install base's. They were Foxglove's always-visible
     panel expand (foxglove-multipanel-tabbed-workspace.png) applied to the
     wrong problem: this band holds four containers and five robots, so
     expanding either one painted a four-row table into a 1235x828 pane and
     left half the viewport flat charcoal. They existed only because the
     default layout starved both tables — and the fix for a starved table is
     to stop starving it, not to add a mode that blanks the surface to read
     it. The readiness facts left the band for the identity block above, the
     204px they were holding went to the install base, and both tables now
     carry every column at 1600 that they carry at 1280. What still does not
     FIT vertically is named and paged by the panel's own footer strip, in
     place, with the rest of the surface still on screen. */

  const manifestRows = containers.length;
  const rolloutRows = pinned?.length ?? 0;
  /* Two panels abreast share one row; stacked, the manifest band and the
     install base are two rows and each gets half of what is going spare. */
  const budget = wide
    ? mainH - IDENT_H - 2 * 3 - SCRIPT_MIN
    : (mainH - IDENT_H - 3 * 3 - SCRIPT_MIN) / 2;
  const bandCap =
    mainH === 0
      ? 5
      : clampInt(4, Math.floor((budget - PANEL_CHROME - MORE_H) / ROW_H), BAND_ROWS_MAX);
  const bandA = panelHeightFor(manifestRows, bandCap);
  /* The install-base panel puts a two-line alert above its rows when robots
     are already pinned to a revision that cannot provision them, and that
     alert is the last thing on this surface that should cost a row. */
  const strand = revision !== null && !revision.complete && rolloutRows > 0 ? 46 : 0;
  const bandB = panelHeightFor(rolloutRows, bandCap) + strand;

  /* ---- the ledger's columns, along either axis ---------------------------

     THE MANIFEST HAS TWO NEIGHBOURHOODS, AND THE CATALOG ONLY EVER SHOWED ONE.

     Along one axis a revision's siblings are the other revisions of its own
     morphology, and the question is "what did this release move" — the column
     set this ledger has always had.

     Along the other, three of the four containers in every manifest here are
     the SAME software on every morphology: `agent`, `capture` and `teleop` are
     the common set, and only `core` is per-morphology (PRD-0007/0008's version
     manifest). This catalog currently pins the agent at four different builds
     across four morphologies — 2.11.3 on jetrover/rev-d, 2.10.7 on rover and
     wukong, 2.9.4 on koko — and there was nowhere on this surface that fact
     could be seen. "Is every morphology's head revision on the same agent" is
     a fleet question, and a fleet-configuration console has to answer it.

     So the ledger takes an axis. Same table, same cells, same adopt
     affordance; only the column set changes, and the header names which axis
     it is on. Cross-morphology columns are the OTHER morphology's newest
     revision, because that is the one its next reflash will use. */

  const [ledgerAxis, setLedgerAxis] = useState<'revision' | 'morphology'>('revision');

  /** One ledger column, read off the same per-revision manifest map the change
      set is graded against. */
  const columnFor = useCallback(
    (m: string, r: CatalogRevision, label: string, name: string, cross: boolean) => {
      const read = manifests.get(keyOf(m, r.version)) ?? LOADING;
      const rows = held(read);
      const byName = new Map<string, string>();
      for (const c of rows?.containers ?? []) {
        if (c.value !== null && c.value !== '') byName.set(c.name, c.value);
      }
      /* A column whose own manifest did not come back prints `—` in every
         cell, and its header says so — otherwise "this revision has no entry
         for `teleop`" and "we could not read this revision" render as the same
         em dash. */
      return {
        key: keyOf(m, r.version),
        label,
        name,
        byName,
        unread: rows === null,
        why: failure(read),
        /* This column shows what the OTHER revision has published. If that
           revision is itself holding an unpublished draft, the column is
           still true — but only if it says so, otherwise a reviewer reads
           `2.12.0` here and publishes `2.12.9` from the rail. */
        staged: stagedByKey.get(keyOf(m, r.version)) ?? 0,
        cross,
      };
    },
    [manifests, stagedByKey],
  );

  const ledgerRevisions = useMemo(
    () =>
      revisions
        .filter((e) => e.version !== version)
        .map((r) => columnFor(morphology ?? '', r, r.version, r.version, false))
        /* Three, because past that the ref column — the one cell here that
           turns width into legibility — starts to starve, and the fourth
           revision back is a click away in the rail. */
        .slice(0, 3),
    [revisions, version, morphology, columnFor],
  );

  /** Every OTHER morphology's newest revision, in rail order. Newest by
      `created_at`, not by list position: what a machine of that shape would be
      flashed with today is the head of its own branch, and that is the only
      revision of it worth putting beside this one. */
  const ledgerMorphologies = useMemo(
    () =>
      morphList
        .filter((m) => m.morphology_id !== morphology)
        .map((m) => {
          const revs = held(byMorph.get(m.morphology_id) ?? LOADING) ?? [];
          let head: CatalogRevision | null = null;
          for (const r of revs) {
            if (head === null || Date.parse(r.created_at) > Date.parse(head.created_at)) {
              head = r;
            }
          }
          return head === null
            ? null
            : columnFor(
                m.morphology_id,
                head,
                m.morphology_id,
                `${m.morphology_id} ${head.version}`,
                true,
              );
        })
        .filter((c): c is NonNullable<typeof c> => c !== null)
        .slice(0, 3),
    [morphList, morphology, byMorph, columnFor],
  );

  const crossable = ledgerMorphologies.length > 0;
  const acrossMorphologies = ledgerAxis === 'morphology' && crossable;
  const ledger = acrossMorphologies ? ledgerMorphologies : ledgerRevisions;

  /** container name → the REPOSITORY this morphology publishes it from, read
      off its own revisions newest-first. It is what decides whether a
      cross-morphology cell is the same software or merely the same word:
      `capture` is `ghcr.io/forest3ai/f3-capture` on every morphology in this
      catalog, and `core` is `f3-jetrover-core` here and `f3-koko-core` there.
      A container this morphology has never pinned has no repository we know
      of, and `null` is the answer — never a guess. */
  const morphRepos = useMemo(() => {
    const out = new Map<string, string>();
    const ordered = [...revisions].sort(
      (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
    );
    for (const r of ordered) {
      const rows = held(manifests.get(keyOf(morphology ?? '', r.version)) ?? LOADING);
      for (const c of rows?.containers ?? []) {
        if (c.value === null || c.value === '') continue;
        if (!out.has(c.name)) out.set(c.name, splitRef(c.value)[0]);
      }
    }
    return out;
  }, [revisions, morphology, manifests]);

  /* The revision the SCRIPT is read against. Revisions come back newest first,
     so the row below the selected one is its predecessor; the oldest revision
     has none, and falls back to its successor rather than to nothing.

     AND IT SKIPS REVISIONS THAT HAVE NO STORED SCRIPT. Every morphology in
     this catalog carries a `001` provisional pin with no installer, and it is
     the immediate predecessor of the revision above it — so the default diff
     used to land on the one target that can only ever answer `+43 −0`, with
     nothing to index beside the code. `script_key` is on the versions list, so
     the nearest sibling that actually HAS an installer is known without
     reading one; that is the default, and the operator can still pick the
     empty one from the select if what they want is "everything is new". */
  const selIndex = revisions.findIndex((r) => r.version === version);
  const defaultCompare = useMemo(() => {
    if (selIndex < 0) return null;
    const has = (i: number): string | null => {
      const r = revisions[i];
      return r !== undefined && r.script_key !== null ? r.version : null;
    };
    for (let i = selIndex + 1; i < revisions.length; i++) {
      const v = has(i);
      if (v !== null) return v;
    }
    for (let i = selIndex - 1; i >= 0; i--) {
      const v = has(i);
      if (v !== null) return v;
    }
    /* Nothing in this morphology has an installer: fall back to the immediate
       neighbour so the select still shows what it is comparing against. */
    return revisions[selIndex + 1]?.version ?? revisions[selIndex - 1]?.version ?? null;
  }, [revisions, selIndex]);
  /* `null` = untouched, so the default stands; `''` = the operator turned the
     diff off and must not have it handed back on the next render. */
  const compareVersion =
    compareSel === null
      ? defaultCompare
      : compareSel === '' || compareSel === version
        ? null
        : compareSel;

  const contentQuery = useBringupContent(morphology, version);
  const scriptRead = readOf(contentQuery);
  const compareQuery = useBringupContent(morphology, compareVersion ?? undefined);
  const compareRead = readOf(compareQuery);
  /* Unread is not empty. With the object store unreachable the stored script
     is not a blank document, it is a document we cannot see — so the editor is
     held read-only, nothing about it can become a staged change, and Publish
     can never write over bytes nobody has looked at. */
  const storedContent = held(scriptRead);
  const scriptKnown = storedContent !== null;
  const storedScript = storedContent?.content ?? '';
  const scriptText = scriptKnown ? (draft.script ?? storedScript) : '';
  const setScript = useCallback(
    (next: string) =>
      stage((d) => ({ ...d, script: next === storedScript ? null : next })),
    [stage, storedScript],
  );

  const manifestDirty = touchesManifest(draft);
  const unpinned = live.filter((c) => imageOf(c).trim() === '');
  const blocked = unpinned[0] ?? null;

  /* ---- what is staged, across the whole catalog --------------------------
     Every dirty revision, graded against its OWN manifest read — not against
     the selected revision's — so the Publish count, the blast radius and the
     rail's marks are all one derivation over one list. */
  const stagedSet = useMemo(() => {
    const order = new Map(
      allRevisions.map((e, i) => [keyOf(e.morphology, e.version), i] as const),
    );
    return Array.from(drafts.entries())
      .map(([key, d]) => {
        const read = manifests.get(key) ?? LOADING;
        const rows = held(read);
        const all = [...(rows?.containers ?? []), ...d.adds.map(stagedContainer)];
        const alive = all.filter((c) => !d.removing.has(c.id));
        const rev = allRevisions.find((e) => keyOf(e.morphology, e.version) === key);
        return {
          key,
          morphology: d.morphology,
          version: d.version,
          draft: d,
          stored: rows?.containers ?? [],
          count: draftCount(d),
          parts: draftParts(d),
          /* The containers this revision would publish with no image. */
          blocked:
            rows === null
              ? null
              : (alive.find((c) => (d.images[c.id] ?? c.value ?? '').trim() === '')
                  ?.name ?? null),
          blockedCount:
            rows === null
              ? 0
              : alive.filter((c) => (d.images[c.id] ?? c.value ?? '').trim() === '')
                  .length,
          /* Every container staged out and none left: a manifest a robot
             cannot provision from at all. */
          emptied: touchesManifest(d) && rows !== null && alive.length === 0,
          /* A manifest change we cannot check against what the revision
             actually holds is a change we must not send: the PUT would be
             written against a set nobody has read. */
          unverifiable: touchesManifest(d) && rows === null,
          robots:
            rev === undefined || byRevisionId === null
              ? null
              : (byRevisionId.get(rev.revision.id)?.length ?? 0),
        };
      })
      .sort((a, b) => (order.get(a.key) ?? 0) - (order.get(b.key) ?? 0));
  }, [drafts, manifests, allRevisions, byRevisionId]);

  const dirty = stagedSet.length > 0;
  const stagedCount = stagedSet.reduce((a, x) => a + x.count, 0);
  const halt =
    stagedSet.find((x) => x.unverifiable || x.emptied || x.blocked !== null) ?? null;
  const canPublish = dirty && halt === null;
  const stagedParts = ['manifest', 'script'].filter((part) =>
    stagedSet.some((x) => x.parts.includes(part)),
  );
  /* A robot pins exactly one revision, so the radius of a multi-revision
     change set is the sum — and it is `—` the moment any one of them is
     unknown, never a lower bound wearing an exact number. */
  const blastKnown = stagedSet.every((x) => x.robots !== null);
  const blastCount = stagedSet.reduce((a, x) => a + (x.robots ?? 0), 0);
  const qc = useQueryClient();
  const [publishing, setPublishing] = useState(false);

  const publish = useCallback(async () => {
    if (!canPublish || publishing) return;
    setPublishing(true);
    const done: string[] = [];
    const failed: string[] = [];
    let last = '';
    for (const x of stagedSet) {
      try {
        await publishRevision(x.draft, x.stored);
        done.push(`${x.morphology} ${x.version}`);
        /* Cleared one at a time: a change set that half-lands must leave the
           half that did not on screen, still staged, still marked. */
        setDrafts((prev) => {
          const map = new Map(prev);
          map.delete(x.key);
          return map;
        });
      } catch (err) {
        failed.push(`${x.morphology} ${x.version}`);
        last = err instanceof Error ? err.message : String(err);
      }
    }
    await qc.invalidateQueries({ queryKey: ['catalog-containers'] });
    await qc.invalidateQueries({ queryKey: ['catalog-versions'] });
    await qc.invalidateQueries({ queryKey: ['bringup-content'] });
    setPublishing(false);
    if (done.length > 0) {
      notifyOk(
        `Published ${plural(done.length, 'revision')}`,
        done.join(' · '),
      );
    }
    if (failed.length > 0) {
      notifyError(`Publish failed — ${failed.join(' · ')}`, last);
    }
  }, [canPublish, publishing, stagedSet, qc]);

  /* ⌘S is printed on the affordance, so it has to work from anywhere on the
     surface — including from inside the script editor. */
  const publishRef = useRef(publish);
  useEffect(() => {
    publishRef.current = publish;
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 's') return;
      e.preventDefault();
      void publishRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const discard = useCallback(() => setDrafts(new Map()), []);

  /* Staged image pins and a staged installer are consequential enough that
     losing them to a stray ⌘R is a real cost. The rail cannot throw them away
     any more; neither should the browser without asking. */
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /* ---- head ------------------------------------------------------------- */
  const board = revision === null ? null : metaString(revision.meta, 'board');
  const validated = revision !== null && revision.meta['validated'] === true;
  const now = useNow();

  /* WHAT SUPERSEDED THIS ONE. A revision is a thing robots are pinned to for
     months, and the first question about any revision that is not the newest
     is which one replaced it — the catalog answered `created 21d ago` and left
     the operator to work the rest out of the rail's ordering by eye.

     Computed off `created_at` rather than off the list's order: the ordering
     the API happens to return is not a fact this readout should depend on, and
     the immediate successor is the one with the smallest creation time greater
     than this revision's. `null` = nothing in this morphology is newer, which
     is the other half of the same answer and gets said out loud. */
  const successor = useMemo(() => {
    if (revision === null) return null;
    const mine = Date.parse(revision.created_at);
    if (Number.isNaN(mine)) return null;
    let best: CatalogRevision | null = null;
    for (const r of revisions) {
      const t = Date.parse(r.created_at);
      if (Number.isNaN(t) || t <= mine || r.version === revision.version) continue;
      if (best === null || t < Date.parse(best.created_at)) best = r;
    }
    return best;
  }, [revision, revisions]);
  const successorRobots =
    successor === null || byRevisionId === null
      ? null
      : (byRevisionId.get(successor.id)?.length ?? 0);

  /* What Publish is about to write, and to whose robots. A revision is what a
     freshly flashed machine pulls and runs as root, so the verb states its
     blast radius — summed across every dirty revision, because the change set
     is what gets committed, not the row the rail happens to be on. */
  const name = (x: { morphology: string; version: string }) =>
    x.morphology === morphology ? x.version : `${x.morphology} ${x.version}`;
  const note = !dirty
    ? 'nothing staged'
    : halt !== null
      ? `${name(halt)} · ${haltReason(halt) ?? 'blocked'}`
      : `${stagedSet.length > 1 ? `${stagedSet.length} revisions · ` : ''}` +
        `${stagedParts.join(' + ')} → ` +
        `${
          !blastKnown
            ? 'blast radius unread'
            : blastCount === 0
              ? 'no robots pinned'
              : plural(blastCount, 'robot')
        }`;
  /* The change set, revision by revision, on the control that commits it —
     because a multi-revision Publish must never be a number you have to trust
     without being able to expand it. */
  const stagedTitle = !dirty
    ? 'Nothing staged — the catalog matches what is published'
    : stagedSet
        .map(
          (x) =>
            `${x.morphology} ${x.version} — ${plural(x.count, 'staged change')}` +
            ` (${x.parts.join(' + ')})` +
            `${x.robots === null ? '; install base unread' : `; ${plural(x.robots, 'robot')} pinned`}`,
        )
        .join('\n');

  return (
    /* The app shell collapses this frame's identity block under its own
       surface strip and hoists only `actions` into it, so the revision's
       identity is carried by the band inside the body instead.

       WHICH IS WHY THE TITLE IS THE SURFACE, NOT THE REVISION. This frame used
       to print `rev-d` here, 30px above a band already reading
       `rev-d jetrover Livox MID-360 + OAK-D Pro, 24V rail` — the same four
       characters twice, in two type steps, on a shell that currently is not
       collapsing anything. It also disagreed with the browser tab, which the
       shell derives from the surface and reads `Hardware revisions · F3 Fleet`
       (THE BAR §8, bonus tells: a page whose title bar and tab disagree about
       what you are looking at). One name per object: the surface names itself
       here, the revision names itself in the band. */
    <SurfaceShell
      /* No crumb either: the shell's own strip 26px above this line already
         reads `Catalog › Hardware revisions`, so an eyebrow here is the word
         `Catalog` printed twice within 40px. The title stands alone and names
         the page — which is also what the browser tab says. */
      title="Hardware revisions"
      /* THE OBJECT, BESIDE THE SURFACE THAT HOLDS IT. The page head is a fixed
         40px band on every route; on this one it was carrying the single word
         the shell's strip already prints 26px above it, while the revision
         named itself again on a 28px line inside the body. The head has the
         room, so the revision takes it — `rev-d jetrover — Livox MID-360 …`,
         the same title+subtitle identity block the robot detail page uses, and
         the body keeps only the 26px readiness line the head has no room for.
         The TITLE stays the surface's own name so it still agrees with the
         browser tab (THE BAR §8, bonus tells). */
      subtitle={
        revision === null || morphology === undefined ? undefined : (
          <span className={styles.identity}>
            <span className={styles.identName}>{revision.version}</span>
            <span className={styles.identMorph}>{morphology}</span>
            {revision.notes !== null && revision.notes !== '' && (
              <span className={styles.identNotes}>{revision.notes}</span>
            )}
          </span>
        )
      }
      actions={
        <>
          <span
            className={styles.stage}
            data-dirty={dirty}
            data-blocked={halt !== null}
            title={stagedTitle}
          >
            {note}
          </span>
          {/* The count, because Discard is not scoped to the revision on
              screen: it drops every staged change in the catalog, and a verb
              that throws away work on three revisions has to say three. */}
          <Button
            badge={dirty ? stagedCount : undefined}
            disabled={!dirty || publishing}
            onClick={discard}
            title={
              dirty
                ? `Discard ${plural(stagedCount, 'staged change')} across ` +
                  `${plural(stagedSet.length, 'revision')}`
                : 'Nothing staged'
            }
          >
            Discard
          </Button>
          <Button
            variant="primary"
            icon="upload"
            kbd="⌘S"
            badge={dirty ? stagedCount : undefined}
            disabled={!canPublish || publishing}
            onClick={() => void publish()}
            title={stagedTitle}
          >
            {publishing ? 'Publishing…' : 'Publish'}
          </Button>
        </>
      }
    >
      <div className={styles.workspace}>
        <CatalogRail
          morphologies={morphList}
          byMorph={byMorph}
          byRevisionId={byRevisionId}
          fleet={fleetRead}
          catalog={catalogRead}
          morphology={morphology}
          version={version}
          staged={stagedByKey}
          review={stagedSet}
          onSelect={select}
        />

        {morphology === undefined || version === undefined || revision === null ? (
          /* THE REVISION SURFACE, WITH NOTHING IN IT. Its manifest table's
             columns are chrome — a revision has containers, images and a
             column per pinned version whether or not this browser managed to
             read one — so the panel keeps them, and the ONE thing that differs
             between "reading", "the catalog is unreadable" and "nothing is
             selected" is the line where the first row would be. */
          <Panel title="Revision" flush loading={catalogRead.fetching}>
            <StoppedContext.Provider
              value={
                catalogRead.phase === 'first' || catalogPending
                  ? { mode: 'reading' }
                  : catalogRead.fault !== null
                    ? { mode: 'stopped' }
                    : { mode: 'empty', note: <Blank noun="revisions">No revision selected.</Blank> }
              }
            >
              <SkeletonTable columns={MANIFEST_COLUMNS} rows={22} what="the manifest" />
            </StoppedContext.Provider>
          </Panel>
        ) : (
          <div
            className={styles.main}
            ref={mainRef}
            style={{
              /* Whole rows, always: a band is `53px of chrome + n × 26px`, so
                 a panel edge can only ever fall between two rows and never
                 through one. */
              ['--band-a' as string]: `${bandA}px`,
              ['--band-b' as string]: `${bandB}px`,
            }}
          >
            {/* READINESS, ON ONE 26px LINE, THE WHOLE WIDTH OF THE SURFACE.

                The identity that used to sit above it is in the page head now
                (see `subtitle`), and the two facts it printed that the panels
                below already print — `4 containers`, `5 robots pinned`, both
                repeated verbatim in a panel header 60px underneath — are gone
                with it. What replaced them is the one thing this surface could
                not answer at all: which revision superseded this one. */}
            <header className={styles.ident}>
              <span
                className={styles.identEdge}
                data-tone={
                  manifestUnread ? 'offline' : revision.bringup_ready ? 'nominal' : 'warn'
                }
                aria-hidden="true"
              />
              {/* Readiness reports what is STORED — what a robot pulling this
                  revision right now would get — not what is staged. The
                  staged set's own completeness is the Publish verb's job. */}
              <ReadinessLine
                revision={revision}
                containers={stored}
                manifestRead={manifestRead}
                scriptLines={
                  scriptKnown && storedScript !== ''
                    ? storedScript.split('\n').length
                    : null
                }
                staged={draftCount(draft)}
                morphology={morphology}
                version={version}
                prov={
                  <span className={styles.prov}>
                    {/* SUCCESSION, FIRST. `created 21d ago` says how old this
                        revision is; it does not say whether anything replaced
                        it, which is the question every revision that is not the
                        head of its morphology raises. Both answers are stated —
                        `latest` is a fact, not the absence of one. */}
                    {successor === null ? (
                      <ProvItem
                        title={`Nothing newer exists for ${morphology} — this revision is the head of the catalog`}
                      >
                        latest
                      </ProvItem>
                    ) : (
                      <ProvItem
                        strong
                        title={
                          `${successor.version} was created after this revision` +
                          `${
                            successorRobots === null
                              ? '; install base unread'
                              : `; ${plural(successorRobots, 'robot')} pinned to it`
                          } — click to open it`
                        }
                      >
                        <button
                          type="button"
                          className={styles.provLink}
                          onClick={() => select(morphology, successor.version)}
                        >
                          superseded by {successor.version}
                        </button>
                      </ProvItem>
                    )}
                    {/* Shed order, dim end first: validation is a flag, the
                        board is hardware provenance, and the age is the one
                        fact on this run that every reference in the set keeps
                        on screen (THE BAR §13, tells 19 and 24). So the age
                        never sheds and `validated` goes first. */}
                    {board !== null && <ProvItem opt="2">{board}</ProvItem>}
                    {validated && <ProvItem opt="1">validated</ProvItem>}
                    <ProvItem title={utc(revision.created_at)}>
                      created {relative(revision.created_at, now)}
                    </ProvItem>
                  </span>
                }
              />
            </header>

            {/* The manifest is sized by the columns it actually has, not by
                the monitor: with one previous revision to show it does not
                need the width of three, and the surplus belongs to the
                install base — where it buys `Site` and `Last seen` — rather
                than to 200px of empty inside a text field. */}
            <div
              className={styles.top}
              style={{
                /* Base measured off the longest reference the registry
                   actually holds (`ghcr.io/forest3ai/f3-jetrover-core:1.0.0`,
                   40 characters) PLUS the staged-value marker, so an edited
                   row does not silently scroll the one string on this surface
                   that has to be read in full before it is published.

                   NOTHING SHEDS FROM EITHER TABLE ANY MORE at any width this
                   surface supports: 464 + 66 per ledger column is what the
                   manifest needs to print every column it has, and the rest of
                   the band — 570px at 1600, 780px at 1920 — is the install
                   base's, which is 30px more than IT needs to print every
                   column it has. Widening the window can only ever add ink
                   here, never subtract it. */
                /* A morphology column carries a longer header (`wukong`, not
                   `rev-c`) so it is budgeted wider — the reference column is
                   the one cell here that turns width into legibility and it
                   must not pay for the axis. */
                ['--manifest-w' as string]: `${464 + (acrossMorphologies ? 78 : 66) * Math.min(3, ledger.length)}px`,
                ['--manifest-w-lg' as string]: `${520 + (acrossMorphologies ? 78 : 66) * Math.min(3, ledger.length)}px`,
              }}
            >
              <ManifestPanel
                containers={containers}
                removing={draft.removing}
                read={manifestRead}
                imageOf={imageOf}
                onEdit={(id, next, base) =>
                  stage((d) => {
                    const images = { ...d.images };
                    /* Typed back to what is stored is not an edit — the entry
                       goes, and the count goes with it. */
                    if (next === base) delete images[id];
                    else images[id] = next;
                    return { ...d, images };
                  })
                }
                onToggleRemove={(id) =>
                  stage((d) => {
                    const next = new Set(d.removing);
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    return { ...d, removing: next };
                  })
                }
                onDropStaged={(id) =>
                  stage((d) => {
                    const images = { ...d.images };
                    delete images[id];
                    return { ...d, images, adds: d.adds.filter((a) => a.id !== id) };
                  })
                }
                onStage={(cname, role) =>
                  stage((d) => ({
                    ...d,
                    adds: [...d.adds, { id: nextStagedId(), name: cname, role }],
                  }))
                }
                onRetry={() => {
                  void qc.invalidateQueries({
                    queryKey: ['catalog-containers', morphology, version],
                  });
                }}
                dirty={manifestDirty}
                blocked={blocked}
                unpinnedCount={unpinned.length}
                ledger={ledger}
                repoOf={morphRepos}
                axis={acrossMorphologies ? 'morphology' : 'revision'}
                crossable={crossable}
                onAxis={setLedgerAxis}
              />
              <RolloutPanel
                robots={pinned}
                fleetError={failure(fleetRead)}
                agentPin={stored.find((c) => c.name === 'agent')?.value ?? null}
                complete={revision.complete}
                onOpen={(id) => setPath(buildPath('fleet', 'detail', id))}
              />
            </div>

            <ScriptPanel
              key={revKey}
              morphology={morphology}
              version={version}
              stored={storedScript}
              read={scriptRead}
              onRetry={() => void contentQuery.refetch()}
              sha={revision.script_sha256 ?? null}
              storageKey={revision.script_key ?? null}
              value={scriptText}
              dirty={draft.script !== null}
              stagedLines={draft.script === null ? null : draft.script.split('\n').length}
              onChange={setScript}
              compareVersion={compareVersion}
              compareKnown={held(compareRead) !== null}
              compareText={held(compareRead)?.content ?? ''}
              compareError={failure(compareRead)}
              compareOptions={revisions
                .filter((r) => r.version !== version)
                .map((r) => r.version)}
              onCompareChange={setCompareSel}
            />
          </div>
        )}
      </div>
    </SurfaceShell>
  );
}

function ProvItem({
  children,
  strong = false,
  opt,
  title,
}: {
  children: ReactNode;
  strong?: boolean | undefined;
  opt?: '1' | '2' | undefined;
  title?: string | undefined;
}) {
  return (
    <>
      <span className={styles.provSep} aria-hidden="true" data-opt={opt}>
        ·
      </span>
      <span className={styles.provItem} data-strong={strong} data-opt={opt} title={title}>
        {children}
      </span>
    </>
  );
}

/* ==========================================================================
   THE RAIL — morphologies → revisions, with readiness as texture.
   ========================================================================== */

interface RailMorphology {
  morphology_id: string;
  name: string;
}

function CatalogRail({
  morphologies,
  byMorph,
  byRevisionId,
  fleet,
  catalog,
  morphology,
  version,
  staged,
  review,
  onSelect,
}: {
  morphologies: readonly RailMorphology[];
  byMorph: Map<string, Read<CatalogRevision[]>>;
  /** `null` — the fleet has not been read, so nothing here may print a count. */
  byRevisionId: Map<number, Robot[]> | null;
  fleet: Read<Robot[]>;
  /** The catalog read, as the state vocabulary words it. */
  catalog: StateRead;
  morphology: string | undefined;
  version: string | undefined;
  /** `morphology/version` → staged mutations, for every dirty revision in the
      catalog. The rail is where an operator finds an edit they left behind on
      a revision they navigated away from, so every one of them is marked. */
  staged: ReadonlyMap<string, number>;
  /** Every dirty revision in the catalog, in rail order — the change set the
      Publish verb is holding, listed rather than hidden in its tooltip. */
  review: readonly Staged[];
  onSelect: (m: string, v?: string) => void;
}) {
  const [adding, setAdding] = useState<string | null>(null);
  const [newMorph, setNewMorph] = useState(false);
  /* A total over a partial read is a lower bound wearing an exact number, so
     the title count goes to `—` until every branch has answered.

     AND `[].every(…)` IS `true`, which is how `Catalog (0)` survived that
     rule: with `/api/morphologies` unanswered there are no branches to check,
     the test passes vacuously, and the reduce over an empty array is a
     confident zero. The trunk has to have answered before its branches can be
     counted — `unread(catalog)` is that premise. */
  const reads = morphologies.map((m) => byMorph.get(m.morphology_id) ?? LOADING);
  const branchesIn = reads.every((r) => held(r) !== null);
  const total = branchesIn && !unread(catalog)
    ? reads.reduce((a, r) => a + (held(r)?.length ?? 0), 0)
    : null;

  /* Roving tabindex, the same contract DataTable holds: exactly ONE revision
     row is in the tab order and the arrows walk the rest. Eleven revisions
     must not cost eleven presses of Tab to step past the rail, and the
     operator navigating by keyboard is navigating WITHIN the catalog, not
     through it. The selected row owns the stop; with nothing selected the
     first row in the tree does. */
  const firstId = morphologies
    .flatMap((m) => held(byMorph.get(m.morphology_id) ?? LOADING) ?? [])
    .find(() => true)?.id;
  const currentId = morphologies
    .flatMap((m) => held(byMorph.get(m.morphology_id) ?? LOADING) ?? [])
    .find((r) => r.morphology_id === morphology && r.version === version)?.id;
  const stopId = currentId ?? firstId ?? null;

  /* Every list is arrow-navigable (THE BAR §6): the rail is one flat sequence
     of revision buttons regardless of which morphology they sit under, so
     ↑/↓ walks the whole catalog and Home/End reach its ends. */
  const onTreeKey = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    const step =
      e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : e.key === 'Home' ? 0 : e.key === 'End' ? 0 : null;
    if (step === null) return;
    const rows = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('[data-revrow]'),
    );
    if (rows.length === 0) return;
    const here = rows.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? rows.length - 1
          : here < 0
            ? 0
            : Math.min(rows.length - 1, Math.max(0, here + step));
    e.preventDefault();
    rows[next]?.focus();
  }, []);

  const [tree, setTree] = useState<HTMLDivElement | null>(null);
  const treeMetrics = useScrollMetrics(tree);
  /* ONE clock for the whole tree rather than one per row: eleven revisions
     printing their age is eleven readings, not eleven subscriptions. */
  const now = useNow();

  return (
    <Panel
      className={styles.rail}
      title="Catalog"
      count={total === null ? NO_VALUE : total}
      pad="none"
      scroll={false}
      loading={catalog.fetching}
      tools={
        <IconButton
          size="sm"
          icon="plus"
          label="New morphology"
          active={newMorph}
          onClick={() => setNewMorph((v) => !v)}
        />
      }
    >
      <div className={styles.railBody}>
      {/* The tree is the one region on this surface whose rows are not a fixed
          pitch, so it cannot be quantised the way the tables are — a part-row
          at the fold IS the affordance there. What it still owes the operator
          is the fact that there is a fold at all, which the platform's fading
          overlay scrollbar does not provide. */}
      <div className={styles.treeBox}>
      <div className={styles.tree} ref={setTree} onKeyDown={onTreeKey}>
      {newMorph && <MorphologyForm onDone={() => setNewMorph(false)} />}

      <ReadBody
        read={catalog}
        skeleton={<SkeletonRail rows={7} what="the catalog" />}
        empty={<Blank noun="morphologies" where="in the catalog" />}
      >
      {morphologies.map((m) => {
        const read = byMorph.get(m.morphology_id) ?? LOADING;
        const revs = held(read) ?? [];
        const revsUnread = held(read) === null;
        const robots =
          revsUnread || byRevisionId === null
            ? null
            : revs.reduce((a, r) => a + (byRevisionId.get(r.id)?.length ?? 0), 0);
        /* So a dirty revision is findable even when the tree has scrolled its
           row out of view. */
        const edits = revs.reduce(
          (a, r) => a + (staged.get(keyOf(m.morphology_id, r.version)) ?? 0),
          0,
        );
        return (
          <section key={m.morphology_id} className={styles.group}>
            <header className={styles.groupHead}>
              <h3 className={styles.groupName} title={m.name}>
                {m.morphology_id}
              </h3>
              <span className={styles.groupCount}>
                ({revsUnread ? NO_VALUE : revs.length})
              </span>
              {edits > 0 && (
                <span
                  className={styles.groupEdits}
                  title={`${plural(edits, 'staged change')} on ${m.morphology_id} revisions, not yet published`}
                >
                  {edits} edited
                </span>
              )}
              <span className={styles.grow} />
              <span
                className={styles.groupRobots}
                title={
                  robots === null
                    ? `Not read — ${failure(read) ?? failure(fleet) ?? 'still reading'}`
                    : `${robots} robots pinned to a ${m.morphology_id} revision`
                }
              >
                {robots === null ? NO_VALUE : robots}
              </span>
              <IconButton
                size="sm"
                icon="plus"
                label={`New ${m.morphology_id} revision`}
                active={adding === m.morphology_id}
                onClick={() =>
                  setAdding((a) => (a === m.morphology_id ? null : m.morphology_id))
                }
              />
            </header>

            {adding === m.morphology_id && (
              <RevisionForm
                morphology={m.morphology_id}
                onDone={(v) => {
                  setAdding(null);
                  onSelect(m.morphology_id, v);
                }}
              />
            )}

            <ul className={styles.list}>
              {revs.map((r) => (
                <RevisionRow
                  key={r.id}
                  revision={r}
                  robots={byRevisionId === null ? null : (byRevisionId.get(r.id)?.length ?? 0)}
                  current={r.morphology_id === morphology && r.version === version}
                  staged={staged.get(keyOf(r.morphology_id, r.version)) ?? 0}
                  stop={r.id === stopId}
                  now={now}
                  onSelect={() => onSelect(r.morphology_id, r.version)}
                />
              ))}
              {read.st === 'failed' && (
                <li>
                  <ErrorLine>revisions unread — {read.why}</ErrorLine>
                </li>
              )}
              {read.st !== 'failed' && revs.length === 0 && (
                <li className={styles.groupEmpty}>
                  {read.st === 'loading' ? 'Reading revisions…' : 'No revisions.'}
                </li>
              )}
            </ul>
          </section>
        );
      })}
      </ReadBody>
      </div>
      <ScrollTrack metrics={treeMetrics} />
      </div>
      <StagedReview review={review} morphology={morphology} onSelect={onSelect} />
      <RailKey />
      <CatalogCoverage byMorph={byMorph} fleet={fleet} />
      </div>
    </Panel>
  );
}

/* --------------------------------------------------------------------------
   THE CHANGE SET, LISTED

   Publishing a revision is a robot downloading and running an installer as
   root, and a change set can span three revisions on two morphologies. Until
   now the only place the whole set was written down was the Publish verb's
   `title`, which is a tooltip — a commit of this weight must be reviewable
   without hovering anything.

   So the rail carries it: one 20px row per dirty revision, the count on the
   right, the halting reason in amber where there is one, and the row itself a
   control that takes you to the revision it names — Skydio's queue → inspect →
   commit, at the scale this surface actually needs. Absent when the catalog
   matches what is published, which is most of the time.
   -------------------------------------------------------------------------- */

function StagedReview({
  review,
  morphology,
  onSelect,
}: {
  review: readonly Staged[];
  /** The morphology on screen; the others print theirs so the row is unique. */
  morphology: string | undefined;
  onSelect: (m: string, v?: string) => void;
}) {
  if (review.length === 0) return null;
  const total = review.reduce((a, x) => a + x.count, 0);
  return (
    <section className={styles.review} aria-label="Staged changes">
      <header className={styles.reviewHead}>
        <h3 className={styles.groupName}>staged</h3>
        <span className={styles.grow} />
        <span className={styles.groupCount}>
          {plural(total, 'change')} · {plural(review.length, 'revision')}
        </span>
      </header>
      <ul className={styles.list}>
        {review.map((x) => {
          const why = haltReason(x);
          return (
            <li key={x.key}>
              <button
                type="button"
                className={styles.reviewRow}
                onClick={() => onSelect(x.morphology, x.version)}
                title={
                  `${x.morphology} ${x.version} — ${plural(x.count, 'staged change')}` +
                  ` (${x.parts.join(' + ')})` +
                  `${x.robots === null ? '; install base unread' : `; ${plural(x.robots, 'robot')} pinned`}` +
                  `${why === null ? '' : `; blocked — ${why}`}`
                }
              >
                <span
                  className={styles.rowEdge}
                  data-tone={why === null ? 'accent' : 'warn'}
                  aria-hidden="true"
                />
                <span className={styles.rowVersion}>{x.version}</span>
                {x.morphology !== morphology && (
                  <span className={styles.reviewMorph}>{x.morphology}</span>
                )}
                <span className={styles.grow} />
                {why === null ? (
                  <span className={styles.reviewParts}>{x.parts.join(' + ')}</span>
                ) : (
                  <span className={styles.rowGap}>{why}</span>
                )}
                <span className={styles.reviewCount}>{x.count}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* --------------------------------------------------------------------------
   What the three cells on every revision row mean, and what the number at the
   row's right edge counts. Drawn with the same `.cell` rule the rows use, so
   the key cannot drift from the thing it names.
   -------------------------------------------------------------------------- */

function RailKey() {
  /* All three at the nominal step: the key names the three POSITIONS, and a
     lone amber sample would be a legend claiming that one of them is special.
     What amber means is already named on the rows that wear it — `incomplete`,
     `no script`, `no payload` — in words, beside the cell. */
  return (
    <div className={styles.railKey}>
      <span className={styles.cells} aria-hidden="true">
        {['m', 's', 'p'].map((k) => (
          <span key={k} className={styles.cell} data-tone="nominal" />
        ))}
      </span>
      <span className={styles.railKeyWord}>manifest · script · payload</span>
      <span className={styles.grow} />
      <span className={styles.railKeyRobots}>robots</span>
    </div>
  );
}

/* --------------------------------------------------------------------------
   The rail's bottom edge carries ambient truth, the way Gotham parks
   `Map health — Connected` bottom-left: for the whole fleet, not the selected
   revision, how much of it is pinned to something that can actually
   re-provision itself. Three facets on a 20px pitch with the counts in their
   own column — a distribution, not a row of pills — because the number that
   matters here is the one that is NOT `bringup ready`.
   -------------------------------------------------------------------------- */

function CatalogCoverage({
  byMorph,
  fleet,
}: {
  byMorph: Map<string, Read<CatalogRevision[]>>;
  fleet: Read<Robot[]>;
}) {
  const roster = held(fleet);

  /* Five facets, and the fifth is the honest one: a robot whose pinned
     revision id is not in any catalog branch we managed to read. Last round
     this surface computed `no revision` by SUBTRACTION — fleet size minus the
     three classified buckets — so on first paint, with no branch answered
     yet, all 28 robots landed in "no revision". A subtraction cannot tell
     "not pinned" from "not read"; reading the robot's own
     `morphology_version_id` can, and the residue gets a category of its own
     instead of being folded into a category that means something else. */
  const items = useMemo(() => {
    const blank = { ready: null, artefact: null, incomplete: null, unresolved: null, none: null } as {
      ready: number | null;
      artefact: number | null;
      incomplete: number | null;
      unresolved: number | null;
      none: number | null;
    };
    const counts = { ...blank };
    if (roster !== null) {
      const flags = new Map<number, CatalogRevision>();
      for (const read of byMorph.values()) {
        for (const r of held(read) ?? []) flags.set(r.id, r);
      }
      counts.ready = 0;
      counts.artefact = 0;
      counts.incomplete = 0;
      counts.unresolved = 0;
      counts.none = 0;
      for (const robot of roster) {
        const id = robot.morphology_version_id;
        if (id === null) {
          counts.none += 1;
          continue;
        }
        const rev = flags.get(id);
        if (rev === undefined) counts.unresolved += 1;
        else if (rev.bringup_ready) counts.ready += 1;
        else if (rev.complete) counts.artefact += 1;
        else counts.incomplete += 1;
      }
    }
    return [
      {
        key: 'ready',
        tone: 'nominal' as Tone,
        count: counts.ready,
        hollow: false,
        label: 'bringup ready',
        title:
          'Pinned to a revision with a complete manifest, a script and a payload digest',
      },
      {
        key: 'artefact',
        tone: 'warn' as Tone,
        count: counts.artefact,
        hollow: false,
        label: 'artefact missing',
        title:
          'Manifest is complete, but the revision has no bringup script or no payload digest — a reflash stops short',
      },
      {
        key: 'incomplete',
        tone: 'error' as Tone,
        count: counts.incomplete,
        hollow: false,
        label: 'no manifest',
        title:
          'Pinned to a revision that does not pin every container image — a reflash cannot provision at all',
      },
      {
        key: 'unresolved',
        tone: 'offline' as Tone,
        count: counts.unresolved,
        hollow: true,
        label: 'revision unread',
        title:
          'Pinned to a revision id that is not in any catalog branch this console could read — the pin is known, the revision behind it is not',
      },
      {
        key: 'none',
        tone: 'offline' as Tone,
        count: counts.none,
        hollow: true,
        label: 'no revision',
        title: 'Not pinned to any hardware revision',
      },
    ];
  }, [byMorph, roster]);

  const fleetWhy = failure(fleet);

  return (
    <section className={styles.coverage} aria-label="Fleet coverage">
      <header className={styles.coverageHead}>
        <h3 className={styles.groupName}>Fleet on this catalog</h3>
        <span className={styles.grow} />
        <span
          className={roster === null ? styles.coverageGap : styles.groupCount}
          title={
            roster !== null
              ? `${roster.length} robots registered`
              : fleetWhy !== null
                ? `/api/robots failed: ${fleetWhy}`
                : 'Still reading; these counts are not yet knowable'
          }
        >
          {plural(roster === null ? null : roster.length, 'robot')}
        </span>
      </header>
      {roster === null && fleetWhy !== null && (
        <span className={styles.coverageWhy} role="alert">
          fleet unread — {fleetWhy}
        </span>
      )}
      <Distribution
        barWidth={104}
        items={items.map((i) => ({
          key: i.key,
          tone: i.tone,
          count: i.count,
          hollow: i.hollow,
          title: i.count === null ? `${i.title} — not read` : i.title,
          label: (
            <StateLabel tone={i.tone} hollow={i.hollow} mono={false} label={i.label} />
          ),
        }))}
      />
    </section>
  );
}

/** manifest / script / payload as three cells — the highest-density encoding
    that fits a 26px rail row, so eleven revisions carry 33 readings. */
function ReadyCells({ revision }: { revision: CatalogRevision }) {
  const cells: { key: string; tone: Tone; hollow: boolean; title: string }[] = [
    {
      key: 'm',
      tone: revision.complete ? 'nominal' : 'warn',
      hollow: false,
      title: revision.complete
        ? 'Manifest: every container has a pinned image'
        : 'Manifest: at least one container has no image',
    },
    {
      key: 's',
      tone: revision.script_key !== null ? 'nominal' : 'warn',
      hollow: false,
      title:
        revision.script_key !== null
          ? 'Bringup script published'
          : 'No bringup script published',
    },
    {
      key: 'p',
      tone: revision.payload_sha256 != null ? 'nominal' : 'warn',
      hollow: false,
      title:
        revision.payload_sha256 != null
          ? 'Payload digest recorded'
          : 'No payload digest recorded',
    },
  ];
  return (
    <span className={styles.cells} aria-hidden="true">
      {cells.map((c) => (
        <span
          key={c.key}
          className={styles.cell}
          data-tone={c.tone}
          data-hollow={c.hollow}
          title={c.title}
        />
      ))}
    </span>
  );
}

function RevisionRow({
  revision,
  robots,
  current,
  staged,
  stop,
  now,
  onSelect,
}: {
  revision: CatalogRevision;
  /** `null` — the fleet has not been read, which is not "none pinned". */
  robots: number | null;
  current: boolean;
  /** Staged mutations held against this revision; 0 = it matches what is
      published. Non-zero turns the leading tick accent and puts the count in
      the row, so an edit left on another revision is visible from here. */
  staged: number;
  /** This row owns the rail's single tab stop; the arrows reach the others. */
  stop: boolean;
  /** The tree's clock, so every row's age is read off the same instant. */
  now: number;
  onSelect: () => void;
}) {
  const gap = !revision.complete
    ? 'incomplete'
    : revision.script_key === null
      ? 'no script'
      : revision.payload_sha256 == null
        ? 'no payload'
        : null;
  /* Red is spent on exactly one thing here: a robot pinned to a revision with
     no complete manifest, which is a robot that cannot be re-provisioned at
     all. A revision merely missing its payload digest still runs on the robots
     already carrying it — the rail's amber gap word is the right weight for
     that, and a fifth red row would devalue the two that matter. */
  const stranded = robots !== null && robots > 0 && !revision.complete;
  const state = revision.bringup_ready ? 'bringup ready' : `not bringup ready — ${gap}`;
  const edited = staged > 0;
  const born = Date.parse(revision.created_at);
  const age = Number.isNaN(born) ? NO_VALUE : duration(now - born, { compact: 1 });

  return (
    <li>
      <button
        type="button"
        data-revrow=""
        className={styles.row}
        tabIndex={stop ? 0 : -1}
        data-current={current}
        aria-current={current ? 'true' : undefined}
        onClick={onSelect}
        title={
          `${revision.morphology_id} ${revision.version} — ${state}; ` +
          (robots === null ? 'install base unread' : `${robots} robots pinned`) +
          `; created ${utc(revision.created_at)}` +
          (edited ? `; ${plural(staged, 'staged change')} not yet published` : '')
        }
      >
        <span
          className={styles.rowEdge}
          data-tone={revision.bringup_ready ? 'nominal' : 'warn'}
          aria-hidden="true"
        />
        {/* TWO LINES, because on one line the note was the first thing to go.
            At a 308px rail the single-line row gave the note 115–187px and ten
            of eleven revisions truncated — four of them to the identical,
            useless `Provisional pin (pr…`, which is the one string that has to
            discriminate `rev-b` from `rev-c` before you click either. The note
            is the row's discriminator, so it gets the row's full width on its
            own line at the dim step: Foxglove's recording rows
            (`foxglove-device-detail-recordings.png`) and Skydio's queue cards
            (`skydio-dfr-incident-dispatch.png`) both put the identity on line
            one and the discriminating detail dim under it. 42px, THE BAR §2's
            two-line pitch. */}
        <span className={styles.rowBody}>
          <span className={styles.rowTop}>
            <span className={styles.rowVersion}>{revision.version}</span>
            <span className={styles.grow} />
            {/* A revision holding an unpublished draft says so in the word
                slot, and the readiness gap it also has moves into the title:
                what needs a decision now is the edit, and the three readiness
                cells beside it still carry the gap without spending a word. */}
            {edited ? (
              <span className={styles.rowEdit}>edited {staged}</span>
            ) : (
              gap !== null && <span className={styles.rowGap}>{gap}</span>
            )}
            <ReadyCells revision={revision} />
            {/* `0` is a reading; `—` is the absence of one. They are two facts
                and they get two glyphs and two weights. */}
            <span
              className={styles.rowRobots}
              data-stranded={stranded}
              data-zero={robots === 0}
              data-unread={robots === null}
            >
              {robots === null ? NO_VALUE : robots}
            </span>
          </span>
          {/* THE ROW'S AGE, at the note's trailing edge. A catalog is a
              release history and this rail was printing none of it: eleven
              revisions, no dates, and the operator working the ordering out of
              the list's sequence by eye. Truncated, never rounded (`format.ts`
              `duration`), so `148d` is a floor and not a claim. Same idiom as
              the mono age chip Skydio right-aligns on every queue card
              (skydio-dfr-incident-dispatch.png). */}
          <span className={styles.rowBottom}>
            <span className={styles.rowNotes}>{revision.notes ?? ''}</span>
            <span className={styles.rowAge} title={utc(revision.created_at)}>
              {age}
            </span>
          </span>
        </span>
      </button>
    </li>
  );
}

function MorphologyForm({ onDone }: { onDone: () => void }) {
  const create = useCreateMorphology();
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  return (
    <form
      className={styles.inlineForm}
      onSubmit={(e) => {
        e.preventDefault();
        if (id.trim() === '' || name.trim() === '') return;
        create.mutate(
          { morphology_id: id.trim(), name: name.trim() },
          {
            onSuccess: () => {
              setId('');
              setName('');
              onDone();
            },
          },
        );
      }}
    >
      <Input
        size="sm"
        mono
        autoFocus
        placeholder="slug"
        aria-label="Morphology slug"
        value={id}
        onChange={(e) => setId(e.target.value)}
      />
      <Input
        size="sm"
        placeholder="name"
        aria-label="Morphology name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Button size="sm" type="submit" disabled={create.isPending}>
        Add
      </Button>
      {create.isError && <ErrorLine>{String(create.error)}</ErrorLine>}
    </form>
  );
}

function RevisionForm({
  morphology,
  onDone,
}: {
  morphology: string;
  onDone: (version: string) => void;
}) {
  const create = useCreateVersion(morphology);
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  return (
    <form
      className={styles.inlineForm}
      onSubmit={(e) => {
        e.preventDefault();
        const v = version.trim();
        if (v === '') return;
        const notesTrimmed = notes.trim();
        create.mutate(notesTrimmed === '' ? { version: v } : { version: v, notes: notesTrimmed }, {
          onSuccess: () => {
            setVersion('');
            setNotes('');
            onDone(v);
          },
        });
      }}
    >
      <Input
        size="sm"
        mono
        autoFocus
        placeholder="rev-e"
        aria-label={`New ${morphology} revision`}
        value={version}
        onChange={(e) => setVersion(e.target.value)}
      />
      <Input
        size="sm"
        placeholder="notes"
        aria-label="Revision notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <Button size="sm" type="submit" disabled={create.isPending}>
        Add
      </Button>
      {create.isError && <ErrorLine>{String(create.error)}</ErrorLine>}
    </form>
  );
}

/* ==========================================================================
   THE MANIFEST — an editor that is also a diff.
   ========================================================================== */

interface LedgerEntry {
  /** `morphology/version` — unique whichever axis the ledger is on. */
  key: string;
  /** What the column header prints: `rev-c` along the revision axis, `rover`
      along the morphology axis, where the column IS that morphology's head. */
  label: string;
  /** The full name, for every title and aria string: `rev-c` / `rover rev-c`. */
  name: string;
  byName: Map<string, string>;
  /** This revision's own manifest did not come back — its column is unknown,
      not empty, and every cell in it says so. */
  unread: boolean;
  why: string | null;
  /** Staged, unpublished mutations held against the revision this column is
      for. The cells still show what is published; the header says the two are
      about to diverge. */
  staged: number;
  /** This column belongs to a DIFFERENT morphology. `agent`, `capture` and
      `teleop` are the common set and adopting across is exactly the point;
      `core` is per-morphology and its repository differs, so adopting it would
      stage a rover's core image onto a jetrover. Cells whose repository does
      not match are readouts rather than controls — see `CompareCell`. */
  cross: boolean;
}

function ManifestPanel({
  containers,
  removing,
  read,
  imageOf,
  onEdit,
  onToggleRemove,
  onDropStaged,
  onStage,
  onRetry,
  dirty,
  blocked,
  unpinnedCount,
  ledger,
  repoOf,
  axis,
  crossable,
  onAxis,
}: {
  /** Stored containers plus the staged additions, in one list. */
  containers: readonly Container[];
  /** Ids staged for deletion. Nothing has been sent yet. */
  removing: ReadonlySet<number>;
  read: Read<Manifest>;
  imageOf: (c: Container) => string;
  /** `base` is what the server holds, so an edit typed back to it can be
      recognised as no edit at all rather than kept as a phantom change. */
  onEdit: (id: number, value: string, base: string) => void;
  onToggleRemove: (id: number) => void;
  onDropStaged: (id: number) => void;
  onStage: (name: string, role: ContainerRole) => void;
  onRetry: () => void;
  dirty: boolean;
  /** The container this revision would publish with no image, if any, and how
      many there are in total. */
  blocked: Container | null;
  unpinnedCount: number;
  /** The comparison columns, along whichever axis is selected. */
  ledger: readonly LedgerEntry[];
  /** container name → the repository THIS morphology publishes it from. */
  repoOf: ReadonlyMap<string, string>;
  /** Which neighbourhood the columns are from. */
  axis: 'revision' | 'morphology';
  /** There is at least one other morphology to compare against. */
  crossable: boolean;
  onAxis: (next: 'revision' | 'morphology') => void;
}) {
  const [adding, setAdding] = useState(false);
  const unread = held(read) === null;
  const why = failure(read);
  const live = containers.filter((c) => !removing.has(c.id));
  const valued = live.filter((c) => imageOf(c).trim() !== '').length;

  /* How many revisions the ledger can show without starving the reference.
     DataTable sheds a column only when the remaining set cannot fit at its
     content width — and an <input> has no content width, so it would silently
     absorb the shortfall and clip the one string on this surface that must be
     read in full. So the *set* is decided here, against the panel's measured
     width, and the priority is explicit: the reference first, then as many
     revisions as fit, then the role. */
  const [avail, setAvail] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = boxRef.current;
    if (el === null) return;
    setAvail(el.clientWidth);
    const ro = new ResizeObserver(() => setAvail(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  /* Measured off this table at 1600: name 90 + remove 30, one revision column
     66, and 296 for the longest reference the registry actually holds
     (`ghcr.io/forest3ai/f3-jetrover-core:1.0.0`) plus the cell's insets. */
  const per = axis === 'morphology' ? 78 : 66;
  const room = avail === 0 ? Infinity : avail - 120 - 296;
  const shownLedger = ledger.slice(0, Math.max(0, Math.min(3, Math.floor(room / per))));

  const columns: Column<Container>[] = [
    /* Pinned widths, deliberately: a container name and a role do not get
       longer because the monitor did, and every pixel they would have taken
       as a share of the surplus is a pixel off the image reference — which is
       the one cell here that genuinely turns width into legibility. */
    {
      key: 'name',
      header: 'Container',
      kind: 'enum',
      width: '13ch',
      mono: true,
      /* ROLE IS NOT A COLUMN. It was a 7ch one, and it read `both` on all 44
         containers this registry holds — a constant given the same width as
         the data, which then sheared off at 1600 and came back at 1280 so the
         wider monitor showed less. A field with one interesting value out of
         three belongs in the identity cell it qualifies, printed only when it
         is NOT the default: `nav bootstrap` says the thing the column never
         did, `agent` says nothing was there to say. The full role is on every
         row's title either way. */
      cell: (c) => (
        <span className={styles.cname} title={`${c.name} · runs on ${c.role}`}>
          {c.name}
          {c.role !== 'both' && <span className={styles.crole}>{c.role}</span>}
        </span>
      ),
    },
    {
      key: 'image',
      header: 'Image',
      kind: 'text',
      flex: true,
      cell: (c) => {
        const staged = imageOf(c);
        const before = c.value ?? '';
        const edited = staged.trim() !== before;
        /* Staged for deletion: the row stops being editable and states what
           is about to be dropped, in full, struck. An operator reviewing a
           removal needs to read the image that is leaving. */
        if (removing.has(c.id)) {
          return (
            <span className={styles.imageCell}>
              <s className={styles.dropped} title={`Staged for removal: ${before === '' ? 'no image' : before}`}>
                {before === '' ? NO_VALUE : before}
              </s>
              {/* The mirror of `new`, in the same slot and the same ink: both
                  are marks on the change set, and a manifest under review has
                  to read its arrivals and its departures at the same weight. */}
              <span className={styles.stagedTag} title="Staged — deleted on publish">
                remove
              </span>
            </span>
          );
        }
        return (
          <span className={styles.imageCell}>
            <Input
              bare
              mono
              size="sm"
              spellCheck={false}
              placeholder="repo:tag"
              aria-label={`Image reference for ${c.name}`}
              title={staged === '' ? undefined : staged}
              value={staged}
              onChange={(e) => onEdit(c.id, e.target.value, before)}
            />
            {/* Staged, not committed: what is still stored under the edit
                stays legible so publishing is a review rather than a leap.
                Struck, not prefixed with a word — `was ` is 26px of English
                in the one cell whose width is legibility, and a strikethrough
                says "replaced" without spending any. Strikethrough AND
                colour, so the hue is never the only signal. */}
            {c.id < 0 ? (
              <span className={styles.stagedTag} title="Staged — created on publish">
                new
              </span>
            ) : (
              edited && (
                <s
                  className={styles.was}
                  title={before === '' ? 'No image stored' : `Stored: ${before}`}
                >
                  {before === '' ? NO_VALUE : (splitRef(before)[1] ?? midEllipsis(before, 12))}
                </s>
              )
            )}
          </span>
        );
      },
    },
    /* One column per other revision, newest first. Shed oldest-first when the
       panel cannot hold them all — the ref column is what turns width into
       legibility, and it never starves for a two-year-old tag. */
    ...shownLedger.map((entry) => ({
      key: `col-${entry.key}`,
      header: entry.unread ? (
        <span className={styles.colUnread} title={`${entry.name} manifest unread — ${entry.why ?? 'still reading'}`}>
          {entry.label} {NO_VALUE}
        </span>
      ) : entry.staged > 0 ? (
        <span
          className={styles.colStaged}
          title={`${entry.name} holds ${plural(entry.staged, 'staged change')} — this column shows what it has PUBLISHED, not what is staged on it`}
        >
          {entry.label}
        </span>
      ) : entry.cross ? (
        /* The column is a morphology, and the revision behind it is the one
           fact the header has no room for. It is on every cell's title too;
           this is the header saying it once, for the whole column. */
        <span className={styles.colCross} title={`${entry.name} — this morphology's newest revision`}>
          {entry.label}
        </span>
      ) : (
        entry.label
      ),
      kind: 'id' as const,
      mono: true,
      max: '8ch',
      cell: (c: Container) => (
        <CompareCell
          current={imageOf(c).trim()}
          other={entry.unread ? null : (entry.byName.get(c.name) ?? null)}
          unread={entry.unread}
          why={entry.why}
          label={entry.name}
          cross={entry.cross}
          repoHint={repoOf.get(c.name) ?? null}
          container={c.name}
          onAdopt={
            removing.has(c.id) ? null : (v) => onEdit(c.id, v, c.value ?? '')
          }
        />
      ),
    })),
    {
      key: 'rm',
      header: '',
      kind: 'icon',
      width: '30px',
      align: 'right',
      cell: (c) =>
        removing.has(c.id) ? (
          <IconButton
            size="sm"
            icon="history"
            label={`Keep ${c.name} in the manifest`}
            title={`Keep ${c.name} — cancels the staged removal`}
            onClick={() => onToggleRemove(c.id)}
          />
        ) : (
          <IconButton
            size="sm"
            icon="trash"
            label={
              c.id < 0
                ? `Drop the staged ${c.name} container`
                : `Stage ${c.name} for removal from the manifest`
            }
            title={
              c.id < 0
                ? `Drop ${c.name} — it was never written`
                : `Remove ${c.name} on publish`
            }
            onClick={() => (c.id < 0 ? onDropStaged(c.id) : onToggleRemove(c.id))}
          />
        ),
    },
  ];

  return (
    <Panel
      className={styles.manifest}
      title="Container manifest"
      count={unread ? NO_VALUE : live.length}
      loading={read.st === 'loading'}
      pad="none"
      meta={
        unread ? (
          /* Not `0/0`. A manifest we could not read has no pinned count, and
             a zero here is the number an operator would publish against. */
          <span
            className={styles.count}
            data-tone="offline"
            title={`Manifest unread — ${why ?? 'still reading'}`}
          >
            {NO_VALUE} pinned
          </span>
        ) : (
          <span
            className={styles.count}
            data-tone={valued === live.length ? 'nominal' : 'warn'}
            title={`${valued} of the ${live.length} containers this revision will hold have a pinned image`}
          >
            {valued}/{live.length} pinned
            {/* One name where there is one; a count where there are three,
                because naming only the first of three reads as "fix this one
                and you can publish". */}
            {unpinnedCount > 0 && (
              <span className={styles.gapNote}>
                {' · '}
                {unpinnedCount === 1
                  ? `${blocked?.name ?? ''} needs an image`
                  : `${unpinnedCount} containers need an image`}
              </span>
            )}
            {unpinnedCount === 0 && dirty && (
              <span className={styles.editNote}> · edited</span>
            )}
          </span>
        )
      }
      tools={
        <>
          {/* Recovery lives in the panel that failed, not in a page reload. */}
          {unread && (
            <IconButton
              size="sm"
              icon="refresh"
              label="Re-read this revision's container manifest"
              disabled={read.st === 'loading'}
              onClick={onRetry}
            />
          )}
          {/* WHICH NEIGHBOURHOOD THE COLUMNS COME FROM. Same control idiom as
              the bringup script's `vs rev-c` select 30px below — both name the
              thing the panel is diffing against, both are a view and never a
              mutation. Absent when there is only one morphology in the catalog,
              because then there is only one axis. */}
          {crossable && (
            <Select
              size="sm"
              mono
              className={styles.axisSelect}
              aria-label="Compare this manifest against"
              value={axis}
              onChange={(e) => onAxis(e.target.value as 'revision' | 'morphology')}
              title={
                axis === 'revision'
                  ? 'Columns are the other revisions of this morphology, newest first'
                  : 'Columns are the newest revision of each other morphology — agent, capture and teleop are one image across the fleet'
              }
            >
              <option value="revision">vs revisions</option>
              <option value="morphology">vs morphologies</option>
            </Select>
          )}
          <IconButton
            size="sm"
            icon="plus"
            label="Add a container to this manifest"
            active={adding}
            disabled={unread}
            title={
              unread
                ? 'The manifest could not be read — adding to it would publish against an unknown set'
                : 'Add a container to this manifest'
            }
            onClick={() => setAdding((v) => !v)}
          />
        </>
      }
    >
      {/* The add form sits ABOVE the rows, attached to the `+` that opened it,
          so it displaces the table rather than hiding under a scroll region
          the operator would have to find. */}
      {adding && !unread && (
        <ContainerForm
          taken={containers.map((c) => c.name)}
          onStage={(name, role) => {
            onStage(name, role);
            setAdding(false);
          }}
        />
      )}
      <RowScroll rows={containers.length} noun="containers">
      <div ref={boxRef}>
        <DataTable
          rows={containers}
          columns={columns}
          rowKey={(c) => String(c.id)}
          /* Grey for a row on its way out, amber for a row with no image, and
             nothing at all for a row that is fine — so the edge bar marks the
             two rows that need a decision and no others. */
          rowTone={(c) =>
            removing.has(c.id) ? 'offline' : imageOf(c).trim() === '' ? 'warn' : null
          }
          empty={
            read.st === 'loading'
              ? 'Reading the manifest…'
              : 'No containers in this manifest.'
          }
          error={why === null ? null : `manifest unread — ${why}`}
        />
      </div>
      </RowScroll>
    </Panel>
  );
}

/** The previous revision's value for the same container. `=` where nothing
    moved: the point of the column is the rows that are NOT `=`.

    A cell that DOES differ is a control. The commonest thing that happens in
    this catalog is a new revision carrying most of the previous one's manifest
    — jetrover rev-d differs from rev-c on exactly one of its four containers —
    and until now adopting a value meant retyping
    `ghcr.io/forest3ai/f3-jetrover-core:1.0.0` by hand out of a cell 200px to
    the right that was only showing you its tag. Clicking stages the OTHER
    revision's full reference onto this row; it is a staged change like any
    other, counted by Publish, reversible by Discard, and nothing is sent.
    (THE BAR §13 tell 31: direct manipulation stages, it never dispatches.) */
function CompareCell({
  current,
  other,
  unread,
  why,
  label,
  cross,
  repoHint,
  onAdopt,
  container,
}: {
  current: string;
  other: string | null;
  unread: boolean;
  why: string | null;
  label: string;
  /** The column belongs to another morphology. */
  cross: boolean;
  /** The repository this morphology publishes this container from, when the
      row itself has no image to read one off. `null` — unknown, and a
      cross-morphology cell then adopts nothing rather than guessing. */
  repoHint: string | null;
  /** Stage `other` as this row's image. Absent while the row is not editable
      (staged for removal), which is the one case where adopting makes no
      sense. */
  onAdopt: ((value: string) => void) | null;
  container: string;
}) {
  if (unread) {
    return (
      <span
        className={styles.unread}
        title={`${label} manifest unread — ${why ?? 'still reading'}`}
      >
        {NO_VALUE}
      </span>
    );
  }
  if (other === null) {
    return <span className={styles.dim} title={`No ${label} entry for this container`}>{NO_VALUE}</span>;
  }
  if (other === current) {
    return (
      <span className={styles.same} title={`Unchanged from ${label}`}>
        =
      </span>
    );
  }
  /* Same repository — which is the normal case within one morphology — and the
     only thing that moved is the tag, so the tag is the whole cell. A cell
     with nothing to compare against (this container has no image yet) is the
     same situation: there is no repo divergence to report. Only a genuine
     change of repository earns the full reference. */
  const [repoNow] = splitRef(current);
  const [repoThen, tagThen] = splitRef(other);
  /* An unvalued row has no repository of its own, so within one morphology the
     other revision's is taken as read — that is exactly the case adopting
     exists for. Across morphologies it is not: `core` is a different build on
     every morphology, and a blank cell must not make a koko core look like a
     jetrover one. There the morphology's OWN revisions answer it. */
  const mine = current !== '' ? repoNow : cross ? repoHint : repoThen;
  const sameRepo = mine === repoThen;
  const shown = sameRepo && tagThen !== null ? tagThen : midEllipsis(other, 14);
  /* ACROSS MORPHOLOGIES, ONLY THE COMMON SET IS ADOPTABLE. `agent`, `capture`
     and `teleop` are one image everywhere and comparing them across the
     catalog is the whole reason that axis exists; `core` is built per
     morphology, so its repository differs and staging a rover's core onto a
     jetrover would be a manifest that cannot boot. Same repository = the same
     software = a legitimate adopt; a different one is a readout. */
  if (cross && !sameRepo) {
    /* `core` is built per morphology, so this column's `core` is a DIFFERENT
       piece of software with its own version line — `0.9.4` here is not older
       or newer than `1.0.0` there, it is unrelated. Printed dim, marked with
       the console's annotation rule, and not a control: the value is worth
       seeing, the comparison is not worth implying, and adopting it would
       stage a koko image onto a jetrover.

       The tag alone is enough WHEN WE KNOW the repositories differ. When this
       morphology has never pinned this container at all there is no repository
       to compare against, so the reference is printed rather than reduced to a
       tag that would read as commensurable. */
    return (
      <span
        className={styles.crossRef}
        title={
          mine === null
            ? `${label}: ${other} — nothing in this morphology pins ${container}, so there is no image here to compare it with`
            : `${label}: ${other} — a different image from this container's, so the two versions are not comparable`
        }
      >
        {mine === null ? midEllipsis(other, 13) : (tagThen ?? midEllipsis(other, 13))}
      </span>
    );
  }
  if (onAdopt === null) {
    return (
      <span className={styles.changed} title={`${label}: ${other}`}>
        {shown}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={styles.adopt}
      /* The visible text is a bare tag (`1.6.4`), which is not a name a screen
         reader can act on. */
      aria-label={`Stage ${label}'s image ${other} as ${container}'s image`}
      title={`${label}: ${other} — click to stage it as ${container}'s image`}
      onClick={() => onAdopt(other)}
    >
      {shown}
    </button>
  );
}

function ContainerForm({
  taken,
  onStage,
}: {
  /** Names already in the manifest, staged ones included — the server rejects
      a duplicate, and finding that out at publish time would fail the whole
      change set for one typo. */
  taken: readonly string[];
  onStage: (name: string, role: ContainerRole) => void;
}) {
  const [name, setName] = useState('');
  const [role, setRole] = useState<ContainerRole>('both');
  const trimmed = name.trim();
  const clash = trimmed !== '' && taken.includes(trimmed);
  return (
    <form
      className={styles.addRow}
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed === '' || clash) return;
        onStage(trimmed, role);
        setName('');
      }}
    >
      <Input
        size="sm"
        mono
        autoFocus
        placeholder="container name"
        aria-label="New container name"
        aria-invalid={clash}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Select
        size="sm"
        mono
        className={styles.roleSelect}
        aria-label="Container role"
        value={role}
        onChange={(e) => setRole(e.target.value as ContainerRole)}
      >
        {ROLES.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </Select>
      <Button size="sm" type="submit" disabled={trimmed === '' || clash}>
        Stage
      </Button>
      {clash && <span className={styles.formWhy}>{trimmed} is already in this manifest</span>}
    </form>
  );
}

/* ==========================================================================
   READINESS — the verdict, and the evidence under it, ON ONE 26px LINE.

   This was a panel: a 204px column standing in the state band holding four
   static label:value pairs and a lamp. Nothing in it scrolls, sorts, pages or
   grows — four facts, always four, always the same height — and it was
   holding 204px of band away from the install-base table, which then shed
   `Site`, `Status` and `Last seen` at 1600 and handed them back at 1280. A
   wider monitor showing less is not a trade-off, it is a bug.

   So it is a line now, under the revision's name, in the idiom Auterion uses
   for exactly this (auterion-vehicle-detail-overview.png:
   `Auterion Skynode · Quadrotor · AOS v2.17.0 · PX4 2.7.26 · PX4_FMU_V5X ·
   Transfer Locked` on one 20px row). Verdict first because it is the answer,
   evidence after it because a verdict with no evidence makes an operator
   guess which half is missing, and the same per-fact dots the panel had —
   this is still the flight-log review's per-section verdict list
   (auterion-flight-log-review.png), just laid along x instead of y.
   ========================================================================== */

function ReadinessLine({
  revision,
  containers,
  manifestRead,
  scriptLines,
  staged,
  morphology,
  version,
  prov,
}: {
  revision: CatalogRevision;
  containers: readonly Container[];
  /** The manifest read itself, not just its rows: this is an audit, and an
      audit that cannot read its evidence has to say so rather than grade the
      empty set. */
  manifestRead: Read<Manifest>;
  scriptLines: number | null;
  /** Staged mutations held against THIS revision. Non-zero means the numbers
      on this line and the numbers in the manifest editor below it are
      deliberately different — `4/4` here against `4/5` there — and the line
      has to say which of the two it is reporting rather than leave an operator
      to decide that one of them is wrong. */
  staged: number;
  morphology: string;
  version: string;
  /** The revision's provenance — succession, board, validation, age — as one
      `·`-separated mono run. It rides on this line rather than on one of its
      own because none of it scrolls, sorts or grows: Auterion prints exactly
      this class of fact as a single 20px line under the vehicle's name
      (auterion-vehicle-detail-overview.png). */
  prov: ReactNode;
}) {
  const sync = useSyncBringupPayload(morphology, version);
  const manifestUnread = held(manifestRead) === null;
  const manifestWhy = failure(manifestRead);
  const valued = containers.filter((c) => c.value !== null && c.value !== '').length;
  const scriptSha = revision.script_sha256 ?? null;
  const payloadSha = revision.payload_sha256 ?? null;
  const key = revision.script_key ?? revision.payload_key ?? null;
  const prefix = key === null ? null : key.slice(0, key.lastIndexOf('/') + 1);

  const facts: {
    key: string;
    label: string;
    tone: Tone;
    hollow: boolean;
    /** The VALUE is absent — dim it. Separate from the dot's tone, so an
        optional check that is satisfied still prints its value at full ink. */
    missing: boolean;
    value: string;
    /** The full digest behind an abbreviated value, for the copy affordance. */
    copy?: string;
    title: string;
  }[] = [
    {
      key: 'manifest',
      label: 'Manifest',
      /* Grey and hollow, never green: the catalog record's own `complete`
         flag is a claim this line exists to corroborate, and corroborating it
         means reading the containers. */
      tone: manifestUnread ? 'offline' : revision.complete ? 'nominal' : 'warn',
      hollow: manifestUnread,
      missing: manifestUnread,
      /* `4/4`, not `4/4 pinned`: the word is the label's job, and the manifest
         panel's own header prints `4/4 pinned` 30px below this line. */
      value: manifestUnread ? NO_VALUE : `${valued}/${containers.length}`,
      title: manifestUnread
        ? `The container manifest could not be read (${manifestWhy ?? 'still reading'}), ` +
          `so the number of pinned images is unknown. The catalog record says ` +
          `${revision.complete ? 'complete' : 'incomplete'}; this cannot confirm it.`
        : 'Every container needs a pinned image before a robot can be provisioned',
    },
    {
      key: 'script',
      label: 'Script',
      tone: revision.script_key !== null ? 'nominal' : 'warn',
      hollow: false,
      missing: scriptSha === null,
      value: scriptSha !== null ? scriptSha.slice(0, 12) : 'not published',
      ...(scriptSha !== null ? { copy: scriptSha } : {}),
      title:
        scriptSha !== null
          ? `${scriptLines === null ? 'length unread' : `${scriptLines} lines`} · sha256 ${scriptSha}`
          : 'No bringup script stored for this revision',
    },
    {
      key: 'payload',
      label: 'Payload',
      tone: payloadSha !== null ? 'nominal' : 'warn',
      hollow: false,
      missing: payloadSha === null,
      value: payloadSha !== null ? payloadSha.slice(0, 12) : 'not recorded',
      ...(payloadSha !== null ? { copy: payloadSha } : {}),
      title:
        payloadSha !== null
          ? `sha256 ${payloadSha}`
          : 'CI publishes the payload tarball; syncing records the digest of the stored object',
    },
  ];

  /* THE OBJECT PREFIX SITS AT THE FAR END OF THE LINE, IN FULL. It is the one
     fact here that is a location rather than a grade, it is the longest string
     on the line, and the line's right end is where the width is — the same
     place row 1 puts the board and the age. Rerun names the endpoint it is
     reading from in its own title bar (rerun-mcap-lidar-dual-3d-timeline.png);
     this names the bucket path every artefact on this surface is published to,
     so an operator can go and check one by hand. Mid-ellipsised rather than
     truncated: the tenant is at the head and the revision is at the tail, and
     the tail is the half that identifies it. */
  const store =
    prefix === null
      ? { value: 'nothing stored', missing: true, hollow: true }
      : { value: midEllipsis(prefix, 40), missing: false, hollow: false };

  /* The lamp is this line's own verdict, so it is only allowed to be green
     when every required fact beside it was actually read. Held at grey — with
     the reason in the title — otherwise. */
  const unknown = manifestUnread;
  const verdict = unknown
    ? 'readiness unknown'
    : revision.bringup_ready
      ? 'bringup ready'
      : 'not bringup ready';

  return (
    <div className={styles.audit}>
      <StateLabel
        /* `nominal`, not `good`. This is the same component and the same two
           words the rail's coverage block prints 700px below, and they used to
           render in two different hues — which meant one of them was wrong.
           Grey is the one that is right: every other nominal signal on this
           surface (the rail's readiness cells, the row edge ticks, the
           `Polling` dot) is already steel, and a lone green verdict is a fifth
           of the saturated ink on a healthy screen spent saying "fine". */
        tone={unknown ? 'offline' : revision.bringup_ready ? 'nominal' : 'warn'}
        hollow={unknown}
        mono={false}
        loud={!unknown && !revision.bringup_ready}
        label={verdict}
        title={
          unknown
            ? `The manifest could not be read (${manifestWhy ?? 'still reading'}), so this revision cannot be graded`
            : revision.bringup_ready
              ? 'A flashed robot pinned to this revision can provision itself'
              : 'A flashed robot pinned to this revision will not provision'
        }
      />
      <span className={styles.auditRule} aria-hidden="true" />
      <dl className={styles.facts}>
        {facts.map((f) => (
          <div key={f.key} className={styles.fact} title={f.title}>
            <StatusDot tone={f.tone} hollow={f.hollow} />
            <dt className={styles.factLabel}>{f.label}</dt>
            <dd className={styles.factValue} data-missing={f.missing}>
              {/* An abbreviated digest you cannot recover in full is a digest
                  you cannot check an artefact against. The 12 characters read
                  at a glance; the button hands over all 64. */}
              {f.copy === undefined ? (
                f.value
              ) : (
                <button
                  type="button"
                  className={styles.digest}
                  aria-label={`Copy the full ${f.label.toLowerCase()} sha256`}
                  title={`sha256 ${f.copy} — click to copy`}
                  onClick={() => copyDigest(f.label, f.copy as string)}
                >
                  {f.value}
                </button>
              )}
            </dd>
          </div>
        ))}
      </dl>
      <span className={styles.grow} />
      {prov}
      <dl
        className={styles.fact}
        title={
          prefix === null
            ? 'Nothing has been written for this revision yet'
            : `Objects for this revision live under ${prefix}`
        }
      >
        <StatusDot tone="offline" hollow={store.hollow} />
        <dt className={styles.factLabel}>Storage</dt>
        <dd className={styles.factValue} data-missing={store.missing}>
          {store.value}
        </dd>
      </dl>
      {/* Why the lamp is held, and — when the revision holds a draft — WHICH
          revision it is grading. This line reports the stored one, what a
          robot pulling right now would get, so with an edit staged it prints
          `4/4 pinned` while the editor below prints `4/5`. Two true numbers,
          and without this word an operator has to decide which is a bug. */}
      {unknown ? (
        <span className={styles.auditNote}>
          {manifestRead.st === 'loading' ? 'reading manifest' : 'manifest unread'}
        </span>
      ) : (
        staged > 0 && (
          <span
            className={styles.auditNote}
            title={
              `This revision holds ${plural(staged, 'unpublished change')}; every ` +
              'fact on this line reports the stored revision, not the staged one'
            }
          >
            as published
          </span>
        )
      )}
      {/* The failure stays on the line that owns the value it failed to
          refresh, in --error, rather than in a toast that is gone before the
          operator looks back. It costs no layout: the note slot is empty
          whenever there is one to print. */}
      {sync.isError && (
        <span className={styles.auditFail} role="alert" title={String(sync.error)}>
          payload sync failed — {String(sync.error)}
        </span>
      )}
      <IconButton
        size="sm"
        icon="refresh"
        label="Sync the payload digest from storage"
        disabled={sync.isPending}
        onClick={() => sync.mutate()}
      />
    </div>
  );
}

function copyDigest(what: string, sha: string): void {
  void navigator.clipboard
    ?.writeText(sha)
    .then(() => notifyOk(`Copied ${what.toLowerCase()} digest`, `sha256 ${sha.slice(0, 16)}…`))
    .catch(() => notifyError('Could not copy to the clipboard'));
}

/* ==========================================================================
   ROLLOUT — the blast radius. Who is actually running this.
   ========================================================================== */

function RolloutPanel({
  robots,
  fleetError,
  agentPin,
  complete,
  onOpen,
}: {
  /** `null` — /api/robots has not answered. The install base is unknown, and
      an unknown install base is not an empty one: the difference is whether
      publishing this revision touches nobody or touches everybody. */
  robots: readonly Robot[] | null;
  fleetError: string | null;
  agentPin: string | null;
  complete: boolean;
  onOpen: (robotId: string) => void;
}) {
  const feedWord = useFeedWord();
  const pinTag = agentPin === null ? null : splitRef(agentPin)[1];
  const rows = robots ?? [];
  const onPin =
    pinTag === null || robots === null
      ? null
      : robots.filter((r) => r.firmware === pinTag).length;

  const columns: Column<Robot>[] = [
    {
      key: 'id',
      header: 'Robot',
      kind: 'id',
      mono: true,
      /* The row already opens the robot; this makes the same destination one
         the browser understands — ⌘-click, middle-click, copy link address.
         See `components/AppLink`. */
      cell: (r) => <RobotLink id={r.robot_id} className={styles.robotId} />,
    },
    {
      /* LIVENESS AND ITS AGE ARE ONE CELL, not two columns. `Live` is a
         reading OF `last_seen` — the word and the age are the same fact at two
         resolutions, and printing them under two headers 300px apart cost a
         column boundary, ~90px, and the one thing the pair is actually for:
         `Offline 7h 39m` in a single glance. Skydio's dispatch queue does
         exactly this — state at the leading edge, mono age trailing it
         (skydio-dfr-incident-dispatch.png) — and it is what let this table
         keep `Status` at 1600 instead of shedding it. */
      key: 'link',
      header: 'Link',
      kind: 'status',
      drop: 1,
      cell: (r) => {
        /* Projected, not literal — with ONE exception. `matrixTone` spends
           colour only where a human has to act, which is why the off-pin
           builds beside this column get the amber and a healthy row does not.
           But `live` is the state an operator scans a robot table for, and
           projecting it left the word standing alone with no mark at all while
           every other state on the row carried one.

           So this column reads exactly as the fleet register's Link column
           does: the exception states keep the projection, and a live link
           takes its own green. The two tables print the same fact about the
           same robot and must not disagree about its colour. The word stays
           quiet either way — `loud` is false, so only the dot is tinted. */
        const s = LIVENESS_STATE[r.liveness];
        return (
          <span className={styles.link}>
            <StateLabel
              tone={r.liveness === 'live' ? 'live' : matrixTone(s.tone)}
              hollow={s.hollow}
              mono={false}
              label={s.label}
              title={s.hint}
            />
            <Timestamp value={r.last_seen} staleAfter={600} deadAfter={3600} />
          </span>
        );
      },
    },
    {
      key: 'agent',
      header: 'Agent',
      kind: 'id',
      mono: true,
      cell: (r) => {
        if (r.firmware === null) return <span className={styles.dim}>{NO_VALUE}</span>;
        const off = pinTag !== null && r.firmware !== pinTag;
        return (
          <span
            className={styles.agent}
            data-off={off}
            title={
              pinTag === null
                ? `Reported agent build ${r.firmware}`
                : off
                  ? `Reports ${r.firmware}; this revision pins the agent image at ${pinTag}`
                  : `Reports ${r.firmware}, matching the pinned agent image tag`
            }
          >
            {r.firmware}
          </span>
        );
      },
    },
    {
      key: 'site',
      header: 'Site',
      kind: 'enum',
      dim: true,
      drop: 2,
      cell: (r) => metaString(r.meta, 'site') ?? NO_VALUE,
    },
    {
      /* What the robot is DOING, which is a different question from whether
         it is reachable — a live robot mid-mission and a live robot idle are
         not the same thing to publish a root-privileged revision under. */
      key: 'status',
      header: 'Status',
      kind: 'status',
      drop: 3,
      cell: (r) => {
        const s = STATUS_STATE[r.status];
        return (
          <StateLabel
            tone={matrixTone(s.tone)}
            hollow={s.hollow}
            mono={false}
            label={s.label}
            title={s.hint}
          />
        );
      },
    },
  ];

  return (
    <Panel
      className={styles.rollout}
      title="Robots pinned"
      count={robots === null ? NO_VALUE : robots.length}
      pad="none"
      /* The one live panel on a surface the shell reports as `On demand`: the
         catalog is read when you ask for it, the fleet underneath it is not.
         Freshness is answered per panel, so it is answered here. */
      meta={
        <>
          {pinTag !== null && robots !== null && robots.length > 0 && (
            <span
              className={styles.count}
              data-tone={onPin === robots.length ? 'nominal' : 'warn'}
              title={`${onPin} of ${robots.length} pinned robots report the agent build this revision pins (${pinTag})`}
            >
              {onPin}/{robots.length} on {pinTag}
            </span>
          )}
          {/* Per-panel freshness, and it tells the truth when the poll is
              failing: `Polling off` rather than a steel dot over a dead read. */}
          <LiveTag live={robots !== null} label={feedWord} />
        </>
      }
    >
      {robots !== null && robots.length > 0 && !complete && (
        <div className={styles.strand} role="alert">
          <Icon name="alert-triangle" size={13} />
          {robots.length} robot{robots.length === 1 ? '' : 's'} pinned to a revision with
          no complete manifest — a reflash cannot provision.
        </div>
      )}
      <RowScroll rows={rows.length} noun="robots">
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.robot_id}
          rowTone={(r) => LIVENESS_STATE[r.liveness].tone}
          onRowClick={(r) => onOpen(r.robot_id)}
          empty={
            robots === null
              ? 'Reading the fleet…'
              : 'No robots pinned to this revision.'
          }
          error={fleetError === null ? null : `fleet unread — ${fleetError}`}
        />
      </RowScroll>
    </Panel>
  );
}

/* ==========================================================================
   THE BRINGUP SCRIPT — a code surface, not a textarea.
   ========================================================================== */

function ScriptPanel({
  morphology,
  version,
  stored,
  read,
  onRetry,
  sha,
  storageKey,
  value,
  dirty,
  stagedLines,
  onChange,
  compareVersion,
  compareKnown,
  compareText,
  compareError,
  compareOptions,
  onCompareChange,
}: {
  morphology: string;
  version: string;
  stored: string;
  /** The content read. An unreadable script is NOT an empty script: the
      revision's own record still names a 64-character digest for the object,
      and offering to type over bytes nobody has seen is how a robot ends up
      running an installer that was never reviewed. */
  read: Read<BringupContent>;
  onRetry: () => void;
  sha: string | null;
  /** The object the content read is for, from the revision record. */
  storageKey: string | null;
  value: string;
  dirty: boolean;
  /** Lines of an unpublished draft held against this revision. Non-null even
      when the stored bytes cannot be read, so a failed re-read never looks
      like the operator's own edit was thrown away. */
  stagedLines: number | null;
  onChange: (next: string) => void;
  compareVersion: string | null;
  /** The compare target's content actually came back. A revision with NO
      stored script reads back as an empty document, which is a real answer and
      diffs to `+n −0`; a read that failed is not an answer at all. */
  compareKnown: boolean;
  compareText: string;
  compareError: string | null;
  compareOptions: readonly string[];
  onCompareChange: (v: string) => void;
}) {
  const known = held(read) !== null;
  const why = failure(read);
  /* A nonce rides with the line so clicking the same row twice reveals twice —
     an index whose second click does nothing reads as broken. */
  const revealSeq = useRef(0);
  const [reveal, setReveal] = useState<{ line: number; n: number } | null>(null);
  const lines = known ? (value === '' ? 0 : value.split('\n').length) : null;
  const size = known ? new TextEncoder().encode(value).length : null;
  const empty = known && stored === '' && !dirty;

  /* ---- how the row splits ------------------------------------------------
     THE CODE WELL TAKES WHAT THE DOCUMENT NEEDS AND NOT A PIXEL MORE. It used
     to take a flat 67% of the row, which at 1600 was 826px for a script whose
     longest line ends at 604 — 215px of black — while the change index next
     to it clipped five of its twenty lines to an ellipsis in 407px. Both
     panes hold the SAME KIND of string (a line of shell), so a fixed ratio
     can only ever be wrong in one of them: the well is sized to its own
     longest line plus its gutter, and every pixel past that belongs to the
     index, which has more lines to print than it has room for.

     Measured, not guessed, and re-measured as you type — an edit that adds a
     long line widens the well rather than starting a horizontal scroll the
     operator has to find. Bounded at 4000 lines so a pathological paste
     cannot make the split cost O(document) per keystroke. */
  const longest = useMemo(() => {
    const ls = value.split('\n');
    let n = 0;
    for (let i = 0; i < Math.min(ls.length, 4000); i++) {
      const len = ls[i]?.length ?? 0;
      if (len > n) n = len;
    }
    return n;
  }, [value]);
  /* gutter (`digits`ch + 20px + its rule) + the well's own 8px insets, and
     two columns of slack so the last glyph is never against the seam. */
  const digits = Math.max(2, String(Math.max(1, lines ?? 1)).length);
  const codeW = `calc(${longest + digits + 2}ch + 38px)`;

  /* The same question the manifest's diff column answers, for the other half
     of what a robot runs: what moved since the previous revision. Marked in
     the gutter the editor already has rather than in a second pane.

     An EMPTY compare target is not a reason to withhold the summary. A
     revision with no stored script is the case where the answer matters most
     — every line here is new to it — so it diffs to `+43 −0` like any other.
     Only a compare read that never landed suppresses the count, and it says
     so in the same slot rather than leaving `vs 001` standing over nothing. */
  const diff = useMemo(
    () =>
      !known || compareVersion === null || !compareKnown
        ? null
        : diffAgainst(splitLines(value), splitLines(compareText)),
    [known, compareKnown, value, compareText, compareVersion],
  );

  /* The widest row the index will paint, in columns: kind (1) + line number
     (3) + the line itself. Zero when there is no index. */
  const indexCh = useMemo(() => {
    if (diff === null || diff.bounded) return 0;
    let n = 0;
    for (const c of diff.changes) {
      if (c.kind === 'gap') continue;
      if (c.text.length > n) n = c.text.length;
    }
    return n === 0 ? 0 : n + 4;
  }, [diff]);

  const copy = () => {
    void navigator.clipboard
      ?.writeText(value)
      .then(() => notifyOk(`Copied ${morphology} ${version} bringup script`, `${lines} lines`))
      .catch(() => notifyError('Could not copy to the clipboard'));
  };

  return (
    <Panel
      className={styles.scriptPanel}
      title="Bringup script"
      loading={read.st === 'loading'}
      pad="none"
      scroll={false}
      meta={
        <>
          <span
            className={styles.count}
            title={
              known
                ? undefined
                : 'The stored object could not be read, so its length is unknown — the catalog record carries only the digest'
            }
          >
            {lines === null ? NO_VALUE : lines} lines
            <span className={styles.unit}> · </span>
            {size === null ? NO_VALUE : bytes(size)}
          </span>
          {/* One slot, three answers, same width and same position: the count,
              the reason there is no count, or the reason it could not be
              computed. The select never reads `vs 001` over silence. */}
          {known &&
            compareVersion !== null &&
            (diff === null ? (
              <span
                className={styles.diffFail}
                title={
                  compareError !== null
                    ? `${compareVersion} script unread — ${compareError}`
                    : `Reading the ${compareVersion} script…`
                }
              >
                {NO_VALUE} vs {compareVersion}
              </span>
            ) : diff.bounded ? (
              <span
                className={styles.diffFail}
                title={`${version} and ${compareVersion} are too long to diff in the browser`}
              >
                too long vs {compareVersion}
              </span>
            ) : (
              <span
                className={styles.diffCount}
                data-same={diff.added === 0 && diff.removed === 0}
                title={
                  compareText === ''
                    ? `${compareVersion} has no stored script, so all ` +
                      `${diff.added} lines here are new to it`
                    : `${diff.added} lines in ${version} are not in ${compareVersion}; ` +
                      `${diff.removed} lines in ${compareVersion} are not in ${version}`
                }
              >
                +{diff.added} −{diff.removed} vs {compareVersion}
              </span>
            ))}
          <span
            className={styles.sha}
            data-dirty={dirty}
            title={
              dirty
                ? 'Edited — the stored digest is recomputed by the API on publish'
                : sha === null
                  ? 'Nothing stored for this revision'
                  : known
                    ? `sha256 ${sha}`
                    : `The catalog record names sha256 ${sha} for the stored object; its bytes could not be read`
            }
          >
            {dirty ? (
              'edited · digest on publish'
            ) : sha === null ? (
              'no stored object'
            ) : (
              /* The digest OF THE ARTEFACT, beside the artefact, and it hands
                 over all 64 characters — an abbreviation you cannot recover in
                 full is a digest you cannot check a stored object against. Same
                 control and same treatment as the Readiness panel's. */
              <button
                type="button"
                className={styles.digest}
                aria-label="Copy the full bringup script sha256"
                title={`sha256 ${sha} — click to copy`}
                onClick={() => copyDigest('Script', sha)}
              >
                sha256 {sha.slice(0, 16)}
              </button>
            )}
          </span>
        </>
      }
      tools={
        <>
          {!known && (
            <IconButton
              size="sm"
              icon="refresh"
              label="Re-read the stored bringup script"
              disabled={read.st === 'loading'}
              onClick={onRetry}
            />
          )}
          {/* THE TWO WAYS A REVISION GETS ITS FIRST SCRIPT, and both of them
              stage rather than send. The catalog's own history says which one
              is used: rev-d's installer is rev-c's with three lines moved, and
              every `001` in this catalog is a revision with no script standing
              beside a sibling that has one. Retyping 43 lines to get there is
              not a workflow. Offered only while THIS revision has nothing
              stored — past that the editor is the way you change it. */}
          {empty && compareVersion !== null && compareKnown && compareText !== '' && (
            <Button
              size="sm"
              onClick={() => onChange(compareText)}
              title={`Stage ${compareVersion}'s stored bringup script as this revision's — nothing is sent until you publish`}
            >
              Copy {compareVersion}
            </Button>
          )}
          {(empty || !known) && (
            <Button
              size="sm"
              disabled={!known}
              onClick={() => onChange(defaultBringupScript(morphology, version))}
              title={
                known
                  ? 'Load the script the robot repo ships, as a starting point'
                  : 'Disabled — this revision already has a stored script that could not be read, and loading the standard one would stage a replacement for bytes nobody has seen'
              }
            >
              Load standard
            </Button>
          )}
          {compareOptions.length > 0 && (
            <Select
              size="sm"
              mono
              className={styles.compare}
              aria-label="Diff this script against another revision"
              disabled={!known}
              value={compareVersion ?? ''}
              onChange={(e) => onCompareChange(e.target.value)}
            >
              <option value="">no diff</option>
              {compareOptions.map((v) => (
                <option key={v} value={v}>
                  vs {v}
                </option>
              ))}
            </Select>
          )}
          <IconButton
            size="sm"
            icon="copy"
            label="Copy the bringup script"
            disabled={!known || value === ''}
            onClick={copy}
          />
        </>
      }
    >
      {/* The code surface is present whether or not anything is stored: an
          empty revision's script is an empty document you can type into, and
          replacing it with a centred call-to-action would swap a working
          editor for 600px of nothing. One dim line where line 1 would be.

          An UNREADABLE script is the third case and gets neither: the well
          stays, the gutter stays, and where line 1 would be there is one
          --error line naming the failure and one verb to try again. */}
      {known ? (
        <div
          className={styles.codeRow}
          style={{
            ['--code-w' as string]: codeW,
            /* The index's own longest row: `+`, a 3-column line number, and
               the line. Everything past the sum of the two panes' demands goes
               to the WELL rather than to the index — the well is an editor, so
               slack there is room to type; slack in the index is empty rail. */
            ['--index-w' as string]: `calc(${indexCh}ch + 35px)`,
          }}
        >
          <CodeEditor
            value={value}
            onChange={onChange}
            marks={diff?.marks ?? null}
            markTitle={compareVersion === null ? '' : `not in ${compareVersion}`}
            reveal={reveal}
            label={`Bringup script for ${morphology} ${version}`}
            placeholder={
              empty
                ? `No bringup script stored for ${morphology}/${version} — a robot pinned here will not provision.`
                : undefined
            }
          />
          {/* A wide monitor gives this pane 1500px and the longest line in a
              bringup script is 95 characters. The surplus goes to the answer an
              engineer actually came for — WHICH lines moved — as an index, not
              as 600px of black. Clicking one puts the caret on it.

              ONLY WHEN THERE ARE LINES TO INDEX. Two of the three diffs this
              catalog actually produces have nothing to list — `rev-b` against
              `001`, which has no stored script at all, and any pair that is
              identical — and for both the index used to render a 320px column
              holding one sentence and 560px of ground. The sentence is already
              in the header's `+43 −0 vs 001` and its title; the 320px belong to
              the code. */}
          {diff !== null && !diff.bounded && compareVersion !== null &&
            compareText !== '' && diff.changes.length > 0 && (
            <ChangeIndex
              version={version}
              compareVersion={compareVersion}
              diff={diff}
              onReveal={(line) => setReveal({ line, n: revealSeq.current++ })}
            />
          )}
        </div>
      ) : (
        /* The well keeps the shape of the surface it stands in for: an empty
           gutter strip and the code ground beside it, with the failure on the
           line the shebang would be on. A panel that drops to bare ground on a
           failed read reads as a panel that lost its body — this reads as a
           viewport onto an artefact we could not fetch, which is what it is. */
        <div className={styles.code} style={{ ['--code-gutter' as string]: '2ch' }}>
          <div className={styles.gutter} aria-hidden="true" />
          <div className={styles.codeWell}>
          {read.st === 'loading' ? (
            <Empty>Reading {morphology}/{version} bringup script…</Empty>
          ) : (
            <>
              <ErrorLine>script unreadable — {why}</ErrorLine>
              {/* Name the object, in mono, the way Rerun names the endpoint it
                  is waiting on: the digest and the key are on the revision
                  record and survived the failure, so the operator can go and
                  check the artefact by hand. */}
              {storageKey !== null && (
                <div className={styles.codeMeta}>
                  {storageKey}
                  {sha !== null && ` · sha256 ${sha}`}
                </div>
              )}
              {/* A read that failed AFTER the operator typed must not read as
                  "your edit is gone". It is still staged, still counted by
                  Publish, and it comes back the moment the re-read lands. */}
              {stagedLines !== null && (
                <div className={styles.codeMeta} data-held="true">
                  {stagedLines} staged lines held — re-read to keep editing
                </div>
              )}
            </>
          )}
          </div>
        </div>
      )}
    </Panel>
  );
}

/* --------------------------------------------------------------------------
   THE CHANGE INDEX

   `+3 −3 vs rev-c` says how much moved; this says what. Auterion's flight-log
   review puts a per-section index beside the data so you know which part
   failed before you open it (auterion-flight-log-review.png) — same idea, at
   line granularity, for the one artefact on this surface that a robot
   executes as root.

   Greyscale on purpose. A diff is not a fault: `+` is "in this revision" at
   full ink, `−` is "only in the other one" at the dim step, and the glyph — not
   a hue — is what carries the direction (THE BAR §3, §8 tell #9).
   -------------------------------------------------------------------------- */

function ChangeIndex({
  version,
  compareVersion,
  diff,
  onReveal,
}: {
  version: string;
  compareVersion: string;
  /** Non-empty: the caller does not mount this column with nothing to list.
      Two revisions that are identical, and a comparison against a revision
      with no stored script at all, both used to render a 320px column holding
      one sentence over 560px of ground — and the header's own `+0 −0 vs rev-c`
      / `+43 −0 vs 001` already IS that sentence. */
  diff: Diff;
  onReveal: (line: number) => void;
}) {
  const total = diff.added + diff.removed;
  const truncated = diff.changes.length >= CHANGE_CAP;
  /* Roving tabindex, the contract the rail and DataTable already hold: a
     forty-line diff must not cost forty presses of Tab to step past. One stop,
     arrows walk the rest, Home/End reach its ends. */
  const [stop, setStop] = useState(0);
  const onKey = useCallback((e: ReactKeyboardEvent<HTMLUListElement>) => {
    const step =
      e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : e.key === 'Home' || e.key === 'End' ? 0 : null;
    if (step === null) return;
    const rows = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('[data-changerow]'),
    );
    if (rows.length === 0) return;
    const here = rows.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? rows.length - 1
          : Math.min(rows.length - 1, Math.max(0, (here < 0 ? 0 : here) + step));
    e.preventDefault();
    setStop(next);
    rows[next]?.focus();
  }, []);
  const [list, setList] = useState<HTMLUListElement | null>(null);
  const listMetrics = useScrollMetrics(list);
  /* Which row owns the single stop — counted over the clickable rows only,
     because a `−` row has no line in this document to put a caret on. */
  let seat = -1;

  return (
    <aside className={styles.changes} aria-label={`Lines differing from ${compareVersion}`}>
      <header className={styles.changesHead}>
        <h3 className={styles.groupName}>changed vs {compareVersion}</h3>
        <span className={styles.grow} />
        <span className={styles.changesCount}>
          +{diff.added} −{diff.removed}
        </span>
      </header>
      <div className={styles.changeBox}>
      <ul className={styles.changeList} ref={setList} onKeyDown={onKey}>
        {diff.changes.map((c, i) => {
          /* An elided run of unchanged lines. Stated, never silent. */
          if (c.kind === 'gap') {
            return (
              <li key={`gap-${i}`} className={styles.gap} aria-hidden="true">
                {/* The elision glyph sits in the kind column the `+` and `−`
                    rows use, so a run of skipped lines reads as a fold rather
                    than as a row whose line number happens to be 12. */}
                <span className={styles.changeKind}>⋯</span>
                <span className={styles.changeLine}>{c.skipped}</span>
                <span className={styles.gapWord}>unchanged</span>
              </li>
            );
          }
          /* A `+` row and a context row are both lines of the document the
             editor is showing, so both can take a caret; a `−` row is a line of
             the OTHER revision and there is nothing here to put one on. */
          const mineRow = c.kind !== '-';
          if (mineRow) seat += 1;
          const mine = seat;
          return (
            <li key={`${c.kind}${c.line}-${i}`}>
              {mineRow ? (
                <button
                  type="button"
                  data-changerow=""
                  tabIndex={mine === stop ? 0 : -1}
                  className={styles.change}
                  data-kind={c.kind === '+' ? '+' : 'ctx'}
                  title={
                    c.kind === '+'
                      ? `Line ${c.line} of ${version}, not in ${compareVersion} — click to select it`
                      : `Line ${c.line} of ${version}, unchanged — click to select it`
                  }
                  onFocus={() => setStop(mine)}
                  onClick={() => onReveal(c.line)}
                >
                  <span className={styles.changeKind} aria-hidden="true">
                    {c.kind === '+' ? '+' : ''}
                  </span>
                  <span className={styles.changeLine}>{c.line}</span>
                  <span className={styles.changeText}>{c.text.trim() === '' ? ' ' : c.text}</span>
                </button>
              ) : (
                <span
                  className={styles.change}
                  data-kind="-"
                  title={`Line ${c.line} of ${compareVersion}, not in ${version}`}
                >
                  <span className={styles.changeKind} aria-hidden="true">
                    −
                  </span>
                  <span className={styles.changeLine}>{c.line}</span>
                  <span className={styles.changeText}>{c.text.trim() === '' ? ' ' : c.text}</span>
                </span>
              )}
            </li>
          );
        })}
        {truncated && (
          <li className={styles.changesEmpty}>
            listing capped at {CHANGE_CAP} rows — {total} lines changed in all
          </li>
        )}
      </ul>
      <ScrollTrack metrics={listMetrics} />
      </div>
    </aside>
  );
}

/* --------------------------------------------------------------------------
   A line-numbered, syntax-coloured shell editor.

   The highlighted <pre> sits behind a transparent-text <textarea>: the
   textarea is the scroller and owns every native editing and assistive
   behaviour, and the painted layer is translated to match it. One layout, one
   caret, no re-implementation of text editing — and the gutter can never drift
   from the code because it is driven by the same scrollTop.
   -------------------------------------------------------------------------- */

function CodeEditor({
  value,
  onChange,
  marks,
  markTitle,
  reveal,
  label,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  marks: readonly boolean[] | null;
  markTitle: string;
  /** Put the caret on this 1-based line and scroll it into view. The nonce is
      what makes a repeated click on the same line reveal again. */
  reveal: { line: number; n: number } | null;
  label: string;
  placeholder?: string | undefined;
}) {
  const [offset, setOffset] = useState({ top: 0, left: 0 });
  const lines = useMemo(() => value.split('\n'), [value]);
  const painted = useMemo(() => lines.map(shellTokens), [lines]);
  const digits = Math.max(2, String(lines.length).length);
  const taRef = useRef<HTMLTextAreaElement>(null);

  /* Selecting the line rather than only scrolling to it: the operator clicked
     "line 21 moved", and the answer is the line, highlighted, with the caret
     in it ready to edit. */
  const revealN = reveal?.n ?? -1;
  const revealLine = reveal?.line ?? 0;
  useEffect(() => {
    const ta = taRef.current;
    if (ta === null || revealLine < 1) return;
    const before = lines.slice(0, revealLine - 1);
    const start = before.reduce((a, l) => a + l.length + 1, 0);
    const end = start + (lines[revealLine - 1]?.length ?? 0);
    ta.focus({ preventScroll: true });
    ta.setSelectionRange(start, end);
    const row = parseFloat(getComputedStyle(ta).lineHeight);
    if (Number.isFinite(row) && row > 0) {
      ta.scrollTop = Math.max(0, (revealLine - 1) * row - ta.clientHeight / 2 + row);
    }
    setOffset({ top: ta.scrollTop, left: ta.scrollLeft });
    // The nonce is the trigger; `lines` is read, not depended on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealN, revealLine]);

  return (
    <div className={styles.code} style={{ ['--code-gutter' as string]: `${digits}ch` }}>
      <div className={styles.gutter} aria-hidden="true">
        <div
          className={styles.gutterInner}
          style={{ transform: `translateY(${-offset.top}px)` }}
        >
          {lines.map((_, i) => (
            <div
              key={i}
              className={styles.lineNo}
              data-changed={marks?.[i] === true}
              title={marks?.[i] === true ? markTitle : undefined}
            >
              {i + 1}
            </div>
          ))}
        </div>
      </div>
      <div className={styles.pane}>
        <pre
          className={styles.paint}
          aria-hidden="true"
          style={{ transform: `translate(${-offset.left}px, ${-offset.top}px)` }}
        >
          {painted.map((pieces, i) => (
            <div key={i} className={styles.codeLine}>
              {pieces.map((p, j) => (
                <span key={j} data-tok={p.cls}>
                  {p.text}
                </span>
              ))}
            </div>
          ))}
        </pre>
        <textarea
          ref={taRef}
          className={styles.editor}
          value={value}
          aria-label={label}
          placeholder={placeholder}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          wrap="off"
          onChange={(e) => onChange(e.target.value)}
          onScroll={(e) =>
            setOffset({
              top: e.currentTarget.scrollTop,
              left: e.currentTarget.scrollLeft,
            })
          }
        />
      </div>
    </div>
  );
}

/* ---- line diff ----------------------------------------------------------
   A longest-common-subsequence walk, so the marks are the lines that genuinely
   are not in the other revision rather than the lines that happen to sit at a
   different index. Bounded, because an editor must never be the reason a
   console stalls. */

const DIFF_CELLS = 1_000_000;

/** `''` is a document with no lines, not a document with one empty line —
    the difference is `+43 −0` against a revision that has no script versus
    `+43 −1`. */
function splitLines(text: string): string[] {
  return text === '' ? [] : text.split('\n');
}

/** One row of the unified diff. `line` is 1-based and numbered against the
    document that HAS the line, so a `+` or a context row scrolls the editor and
    a `−` row names a line number in the OTHER revision. `gap` stands for a run
    of unchanged lines that were elided, and says how many. */
type Change =
  | { kind: '+' | '-' | ' '; line: number; text: string }
  | { kind: 'gap'; skipped: number };

interface Diff {
  marks: boolean[];
  added: number;
  removed: number;
  /** The unified diff, in document order: every changed line with CONTEXT
      lines either side of it, and a `gap` row wherever a run was elided.
      Bounded — see CHANGE_CAP. */
  changes: Change[];
  /** The comparison was refused, not computed — say so rather than print the
      `+0 −0` an unattempted diff would produce. */
  bounded: boolean;
}

/** Enough to review a bringup script; a list past this is not a list an
    operator reads, and the count in the header still tells the truth. */
const CHANGE_CAP = 400;

/* WHY CONTEXT, WHEN THE EDITOR IS RIGHT THERE.

   The editor shows ONE document. A reviewer approving something a robot will
   fetch and execute as root is reading TWO, and the half that is not on screen
   anywhere else is the `−` half — the lines the previous revision had and this
   one does not. A bare list of differing lines put `− log "fetching payload for
   jetrover/rev-c"` on screen with nothing around it to say where in rev-c it
   sat, and left a 515px column three-quarters empty at 1920 to do it.

   Three lines either side is `git diff`'s own default and it is the amount that
   makes a changed line legible in place. Elided runs are stated, not silently
   skipped, because a diff that quietly drops lines is the same lie as a
   scrubber that paints data where there is none (THE BAR §13 tell 21). */
const CONTEXT = 3;

/** One step of the LCS walk: a line that is in both documents, or in one. */
interface Op {
  k: '=' | '+' | '-';
  /** 1-based line number in the document that has it. */
  line: number;
  text: string;
}

/** Ops → unified rows: changed lines, CONTEXT unchanged lines either side, and
    a `gap` for every run in between. */
function hunks(ops: readonly Op[]): Change[] {
  const keep = new Array<boolean>(ops.length).fill(false);
  ops.forEach((op, i) => {
    if (op.k === '=') return;
    for (let d = -CONTEXT; d <= CONTEXT; d++) {
      const at = i + d;
      if (at >= 0 && at < ops.length) keep[at] = true;
    }
  });
  const rows: Change[] = [];
  let skipped = 0;
  for (let i = 0; i < ops.length && rows.length < CHANGE_CAP; i++) {
    const op = ops[i] as Op;
    if (!keep[i]) {
      skipped += 1;
      continue;
    }
    if (skipped > 0) {
      rows.push({ kind: 'gap', skipped });
      skipped = 0;
    }
    rows.push({ kind: op.k === '=' ? ' ' : op.k, line: op.line, text: op.text });
  }
  /* A trailing run is elided too, and says so — otherwise the pane ends on a
     context line and reads as the end of the document. */
  if (skipped > 0 && rows.length < CHANGE_CAP) rows.push({ kind: 'gap', skipped });
  return rows;
}

function diffAgainst(a: readonly string[], b: readonly string[]): Diff {
  const n = a.length;
  const m = b.length;
  /* One side empty is a complete answer, not a reason to give up: every line
     of the other side is a difference. */
  if (m === 0) {
    return {
      marks: new Array<boolean>(n).fill(true),
      added: n,
      removed: 0,
      changes: a.slice(0, CHANGE_CAP).map((text, i) => ({ kind: '+' as const, line: i + 1, text })),
      bounded: false,
    };
  }
  if (n === 0) {
    return {
      marks: [],
      added: 0,
      removed: m,
      changes: b.slice(0, CHANGE_CAP).map((text, i) => ({ kind: '-' as const, line: i + 1, text })),
      bounded: false,
    };
  }
  if (n * m > DIFF_CELLS) {
    return {
      marks: new Array<boolean>(n).fill(false),
      added: 0,
      removed: 0,
      changes: [],
      bounded: true,
    };
  }
  /* dp[i][j] = length of the LCS of a[i:] and b[j:] */
  const w = m + 1;
  const dp = new Uint32Array((n + 1) * w);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i * w + j] =
        a[i] === b[j]
          ? dp[(i + 1) * w + j + 1]! + 1
          : Math.max(dp[(i + 1) * w + j]!, dp[i * w + j + 1]!);
    }
  }
  const marks = new Array<boolean>(n).fill(false);
  /* The whole walk, unchanged lines included: the hunk pass below needs them
     to hand a changed line its neighbours. */
  const ops: Op[] = [];
  let added = 0;
  let removed = 0;
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ k: '=', line: i + 1, text: a[i] as string });
      i += 1;
      j += 1;
    } else if (dp[(i + 1) * w + j]! >= dp[i * w + j + 1]!) {
      marks[i] = true;
      added += 1;
      ops.push({ k: '+', line: i + 1, text: a[i] as string });
      i += 1;
    } else {
      removed += 1;
      ops.push({ k: '-', line: j + 1, text: b[j] as string });
      j += 1;
    }
  }
  while (i < n) {
    marks[i] = true;
    added += 1;
    ops.push({ k: '+', line: i + 1, text: a[i] as string });
    i += 1;
  }
  while (j < m) {
    removed += 1;
    ops.push({ k: '-', line: j + 1, text: b[j] as string });
    j += 1;
  }
  return { marks, added, removed, changes: hunks(ops), bounded: false };
}

/* ---- the shell highlighter ---------------------------------------------
   Four classes and no more: comment, string, expansion, keyword. A shell
   script's meaning lives in what is expanded and what is quoted — those two
   are exactly what an operator reviewing a root-privileged installer needs to
   see — so nothing else earns a hue. */

const KEYWORDS = new Set([
  'if', 'then', 'else', 'elif', 'fi', 'for', 'in', 'do', 'done', 'while',
  'until', 'case', 'esac', 'function', 'return', 'exit', 'set', 'export',
  'local', 'readonly', 'shift', 'trap', 'break', 'continue',
]);

const SHELL =
  /("(?:[^"\\]|\\.)*")|('(?:[^'\\]|\\.)*')|(\$\{[^}]*\}|\$[A-Za-z_][A-Za-z0-9_]*|\$[?*@#!0-9])|(#.*)$/g;
const EXPANSION = /\$\{[^}]*\}|\$[A-Za-z_][A-Za-z0-9_]*|\$[?*@#!0-9]/g;
const WORD = /[A-Za-z_][A-Za-z0-9_]*/g;

interface Piece {
  cls: 'txt' | 'kw' | 'str' | 'var' | 'com';
  text: string;
}

function pushWords(out: Piece[], text: string): void {
  let last = 0;
  WORD.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = WORD.exec(text)) !== null) {
    if (!KEYWORDS.has(m[0])) continue;
    if (m.index > last) out.push({ cls: 'txt', text: text.slice(last, m.index) });
    out.push({ cls: 'kw', text: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ cls: 'txt', text: text.slice(last) });
}

/** A double-quoted string still expands, so its expansions stay legible. */
function pushQuoted(out: Piece[], text: string): void {
  let last = 0;
  EXPANSION.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = EXPANSION.exec(text)) !== null) {
    if (m.index > last) out.push({ cls: 'str', text: text.slice(last, m.index) });
    out.push({ cls: 'var', text: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ cls: 'str', text: text.slice(last) });
}

function shellTokens(line: string): Piece[] {
  const out: Piece[] = [];
  let last = 0;
  SHELL.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = SHELL.exec(line)) !== null) {
    const g = m as unknown as (string | undefined)[];
    const start = m.index;
    /* `#` only opens a comment at a word boundary — `$#` and `sha256#tag` are
       not comments, and mis-colouring half a line of an installer as dead
       text is exactly the kind of lie a review surface must not tell. */
    if (g[4] !== undefined && start > 0 && !/\s/.test(line[start - 1] ?? '')) continue;
    if (start > last) pushWords(out, line.slice(last, start));
    if (g[1] !== undefined) pushQuoted(out, g[1]);
    else if (g[2] !== undefined) out.push({ cls: 'str', text: g[2] });
    else if (g[3] !== undefined) out.push({ cls: 'var', text: g[3] });
    else if (g[4] !== undefined) {
      out.push({ cls: 'com', text: g[4] });
      return out;
    }
    last = start + m[0].length;
  }
  if (last < line.length) pushWords(out, line.slice(last));
  return out;
}
